import { useState, useMemo, useEffect, useRef, useCallback, createContext, useContext } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Constants from "../../../../Constants.json";
import { buildHomeBundleFromApi } from "./invelpsHomeApiMapper";
import IndexStrategiesPageContainer from "./IndexStrategiesPageContainer";
import TickerSymbolLink from "../../../../components/TickerSymbolLink";
import { navigateToStockDashboard } from "../../../../utils/stockDashboardNav";
import InvelpsTipMark from "../../../../components/InvelpsTooltip";
import MarketPageSubNav from "../../../../components/MarketPageSubNav";
import InvelpsGlobalNav from "../../../../components/InvelpsGlobalNav";
import {
  Area, AreaChart, Bar, BarChart, CartesianGrid, Cell, LabelList,
  Line, LineChart, Pie, PieChart, ReferenceArea, ReferenceLine,
  ResponsiveContainer, Scatter, ScatterChart, Tooltip, XAxis, YAxis, ZAxis,
} from "recharts";

/* ─── TOKENS ─────────────────────────────────────────────────── */
const C = { green:"#6f8a5b", amber:"#caa85e", orange:"#d28a52", red:"#c6523f", gold:"#b59a5b" };

const LIGHT = {
  appBg:"#fbfaf7", paper:"rgba(255,255,255,.94)", paperSolid:"#fff",
  paperAlt:"#f7f5f0", ink:"#18221e", ink2:"#2e3d35", muted:"#7a8a82",
  line:"rgba(30,50,38,.09)", line2:"rgba(30,50,38,.05)",
  nav0:"#2e574f", nav1:"#3d7068", navTxt:"rgba(255,255,255,.62)", rowH:"rgba(181,154,91,.09)",
};
const DARK = {
  appBg:"#0b1220", paper:"rgba(15,23,42,.80)", paperSolid:"#111827",
  paperAlt:"rgba(15,23,42,.55)", ink:"#e2e8f0", ink2:"#cbd5e1", muted:"#94a3b8",
  line:"rgba(148,163,184,.18)", line2:"rgba(148,163,184,.09)",
  nav0:"#0d2018", nav1:"#142d24", navTxt:"rgba(226,232,240,.65)", rowH:"rgba(181,154,91,.09)",
};

/* ─── GLOBAL CSS ──────────────────────────────────────────────── */
const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@600;700&family=IBM+Plex+Mono:wght@400;600;700&display=swap');
*,*::before,*::after{box-sizing:border-box;margin:0;padding:0;}
html{scroll-behavior:smooth;-webkit-text-size-adjust:100%;}
body{font-family:ui-sans-serif,system-ui,-apple-system,'Segoe UI',sans-serif;overscroll-behavior-y:none;}
::-webkit-scrollbar{width:3px;height:3px;}
::-webkit-scrollbar-thumb{background:rgba(181,154,91,.28);border-radius:2px;}
input,button{font-family:inherit;}
input[type=range]{-webkit-appearance:none;background:transparent;cursor:pointer;}
input[type=range]::-webkit-slider-thumb{-webkit-appearance:none;width:13px;height:13px;border-radius:50%;background:#caa85e;}

/* ── Animations ── */
@keyframes fadeUp{from{opacity:0;transform:translateY(10px)}to{opacity:1;transform:translateY(0)}}
@keyframes slideDown{from{opacity:0;transform:translateY(-6px)}to{opacity:1;transform:translateY(0)}}
.fade-up{animation:fadeUp .3s ease both;}
.slide-down{animation:slideDown .15s ease both;}

/* ── Base spacing ── */
.page{max-width:1600px;margin:0 auto;padding:20px 18px 40px;display:flex;flex-direction:column;gap:12px;width:100%;box-sizing:border-box;}
@media(min-width:640px){.page{padding:22px 24px 48px;gap:14px;}}
@media(min-width:1024px){.page{padding:26px 32px 56px;gap:18px;}}

/* ── Card ── */
.card{border-radius:16px;border:1px solid var(--line);background:var(--paper);box-shadow:0 4px 20px rgba(16,36,24,.07);}
@media(min-width:640px){.card{border-radius:20px;}}
@media(min-width:1024px){.card{border-radius:24px;}}
.card-pad{padding:14px;}
@media(min-width:640px){.card-pad{padding:18px 20px;}}
@media(min-width:1024px){.card-pad{padding:22px 24px;}}

/* ── Nav ── */
.nav-desktop{display:none;}
@media(min-width:768px){.nav-desktop{display:flex;align-items:stretch;}}
.nav-mobile-btn{display:flex;}
@media(min-width:768px){.nav-mobile-btn{display:none!important;}}

/* ── Layout grids — all start as 1-col on mobile ── */
.g2{display:grid;grid-template-columns:1fr;gap:12px;}
.g3{display:grid;grid-template-columns:1fr 1fr;gap:8px;}
.g4{display:grid;grid-template-columns:1fr 1fr;gap:8px;}
@media(min-width:480px){.g4{grid-template-columns:repeat(4,1fr);}}
@media(min-width:640px){.g2{grid-template-columns:1fr 1fr;gap:14px;}.g3{grid-template-columns:repeat(3,1fr);gap:12px;}}
@media(min-width:1024px){
  .g-hero{display:grid;grid-template-columns:1.15fr .85fr;gap:20px;align-items:stretch;}
  .g-de{display:grid;grid-template-columns:.72fr 1.28fr;gap:20px;align-items:start;}
  .g-split{display:grid;grid-template-columns:1.3fr .95fr;gap:20px;align-items:start;}
  .g-split2{display:grid;grid-template-columns:1fr 1fr;gap:20px;align-items:start;}
  .g-flow{display:grid;grid-template-columns:.9fr 1.1fr;gap:16px;}
  .g-home-bottom{display:grid;grid-template-columns:1fr 1.1fr 0.9fr;gap:20px;align-items:start;}
}

/* ── Metric grid ── */
.metric-grid{display:grid;grid-template-columns:1fr 1fr;gap:8px;}
@media(min-width:480px){.metric-grid{grid-template-columns:repeat(4,1fr);}}

/* ── Synthesis grid ── */
.synth-grid{display:grid;grid-template-columns:1fr;gap:10px;}
@media(min-width:640px){.synth-grid{grid-template-columns:repeat(3,1fr);}}

/* ── Leadership row ── */
.ld-row{display:grid;grid-template-columns:30px 1fr 60px 58px;gap:8px;align-items:center;}
@media(min-width:480px){.ld-row{grid-template-columns:30px 1fr auto 64px 66px;}}

/* ── Region flow row ── */
.rf-row{display:grid;grid-template-columns:1fr 60px 66px;gap:8px;align-items:center;}

/* ── Opportunities header/row ── */
.opp-row{display:grid;grid-template-columns:1fr 52px 52px 52px;gap:8px;align-items:center;}
@media(min-width:480px){.opp-row{grid-template-columns:1.2fr 52px 50px 50px 50px 58px;}}

/* ── Pillar row ── */
.pillar-row{display:grid;grid-template-columns:1fr 68px 1fr 18px;gap:8px;align-items:center;}
@media(min-width:480px){.pillar-row{grid-template-columns:1fr 80px 160px 20px;}}

/* ── Stock header ── */
.stock-hdr{display:flex;flex-direction:column;gap:10px;}
@media(min-width:480px){.stock-hdr{flex-direction:row;justify-content:space-between;align-items:flex-start;}}

/* ── Screener table ── */
.sc-row{display:grid;grid-template-columns:120px 60px 52px 52px 56px;gap:4px;align-items:center;}
@media(min-width:640px){.sc-row{grid-template-columns:140px 72px 60px 58px 64px 60px 60px 52px;}}

/* ── Screener filter grid ── */
.filter-grid{display:grid;grid-template-columns:1fr 1fr;gap:14px;}
@media(min-width:640px){.filter-grid{grid-template-columns:repeat(4,1fr);}}

/* ── Tabs ── */
.tabs{display:flex;overflow-x:auto;-webkit-overflow-scrolling:touch;scrollbar-width:none;}
.tabs::-webkit-scrollbar{display:none;}

/* ── Pulse chart height ── */
.pulse-h{height:180px;}
@media(min-width:640px){.pulse-h{height:220px;}}

/* ── Engine grid ── */
@media(min-width:1024px){
  .engine-grid{grid-template-columns:1fr!important;}
  .pulse-de-grid{grid-template-columns:1.2fr 1fr!important;}
}

/* ── Hero 4-stat grid ── */
@media(min-width:480px){
  .hero-grid-4 + * , [class*="hero"] .metric-grid { grid-template-columns:repeat(4,1fr)!important; }
}
`;

/* ─── HELPERS ─────────────────────────────────────────────────── */
const fP = (v, d=1) => `${+v>=0?"+":""}${(+v).toFixed(d)}%`;
const fB = (v) => `${+v>=0?"+":""}${Math.round(+v)} bp`;
const pC = (v) => +v>=0 ? C.green : C.red;
const sC = (v) => +v>=75 ? C.green : +v>=55 ? C.amber : C.red;
const sigC = (s) => ["BUY","STRONG_BUY"].includes(s) ? C.green : s==="ACCUMULATE" ? C.amber : C.red;
const indC = (s) => s==="Bullish"||s==="LEADER_CONFIRMED" ? C.green : s==="Bearish"||s==="OUTFLOW" ? C.red : C.amber;

/* ─── ROUTER (React Router, base /capital) ───────────────────── */
const CAPITAL_BASE = "/capital";

function useCapitalRouter() {
  const navigate = useNavigate();
  const location = useLocation();
  const path =
    location.pathname.startsWith(CAPITAL_BASE)
      ? location.pathname.slice(CAPITAL_BASE.length) || "/"
      : "/";
  const nav = useCallback(
    (to) => {
      const t = to.startsWith("/") ? to : `/${to}`;
      if (t.startsWith("/marketflow")) {
        navigate(t);
        return;
      }
      navigate(CAPITAL_BASE + (t === "/" ? "" : t));
    },
    [navigate]
  );
  return { path, nav };
}

function getRoute(path) {
  if (!path || path === "/") return { r: "home", p: {} };
  if (path === "/markets")  return { r: "markets",  p: {} };
  if (path === "/screener") return { r: "screener", p: {} };
  if (path === "/strategies") return { r: "strategies", p: {} };
  const m = path.match(/^\/stock\/(.+)$/);
  if (m) return { r: "stock", p: { sym: decodeURIComponent(m[1]) } };
  return { r: "404", p: {} };
}

/* ─── CSS VARS ────────────────────────────────────────────────── */
function syncTheme(th, isDark) {
  const r = document.documentElement;
  [["--app-bg",th.appBg],["--paper",th.paper],["--paper-alt",th.paperAlt],
   ["--ink",th.ink],["--ink2",th.ink2],["--muted",th.muted],
   ["--line",th.line],["--line2",th.line2],["--row-h",th.rowH]
  ].forEach(([k,v]) => r.style.setProperty(k, v));
  document.body.style.background = isDark
    ? th.appBg
    : "radial-gradient(1200px 400px at 50% 0%, rgba(181,154,91,.12), transparent 55%), linear-gradient(" + th.appBg + ", " + th.appBg + ")";
  document.body.style.color = th.ink;
}

/* ─── SYMBOLS ─────────────────────────────────────────────────── */
const SYMS = [
  {sym:"PRX.AS",  name:"Prosus N.V.",           sec:"Comm. Services"},
  {sym:"TSM",     name:"Taiwan Semiconductor",  sec:"Info. Technology"},
  {sym:"NVDA",    name:"NVIDIA Corporation",    sec:"Info. Technology"},
  {sym:"AZN",     name:"AstraZeneca PLC",       sec:"Health Care"},
  {sym:"KAP.IL",  name:"Kazatomprom",           sec:"Energy"},
  {sym:"CMCX.L",  name:"CMC Markets plc",       sec:"Financials"},
  {sym:"SMDR.JK", name:"PT Samudera Indonesia", sec:"Industrials"},
  {sym:"MRK",     name:"Merck & Co.",           sec:"Health Care"},
  {sym:"HRUM.JK", name:"PT Harum Energy",       sec:"Energy"},
  {sym:"ONEGLOBAL.BO", name:"One Global Service", sec:"Industrials"},
];

/* ─── SEARCH ──────────────────────────────────────────────────── */
function SearchBar({ th, openTicker }) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const hits = useMemo(() => {
    if (!q.trim()) return [];
    const ql = q.toLowerCase();
    return SYMS.filter(s => s.sym.toLowerCase().includes(ql) || s.name.toLowerCase().includes(ql)).slice(0, 5);
  }, [q]);
  useEffect(() => {
    const fn = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", fn);
    return () => document.removeEventListener("mousedown", fn);
  }, []);
  const go = sym => { if (openTicker) openTicker(sym); setQ(""); setOpen(false); };
  return (
    <div ref={ref} style={{ position:"relative", flex:1, maxWidth:240, minWidth:0 }}>
      <div style={{ display:"flex", alignItems:"center", gap:6, height:36, background:"rgba(255,255,255,.13)", border:"1px solid rgba(255,255,255,.18)", borderRadius:10, padding:"0 12px" }}>
        <span style={{ color:"rgba(255,255,255,.45)", fontSize:14, flexShrink:0 }}>⌕</span>
        <input value={q} onChange={e=>{setQ(e.target.value);setOpen(true);}} onFocus={()=>setOpen(true)}
          onKeyDown={e => {
            if (e.key==="Enter" && q.trim()) { const ex=SYMS.find(s=>s.sym.toLowerCase()===q.trim().toLowerCase()); go(ex?ex.sym:q.trim().toUpperCase()); }
            if (e.key==="Escape") { setOpen(false); setQ(""); }
          }}
          placeholder="Symbole…"
          style={{ background:"none", border:"none", outline:"none", fontSize:12, fontWeight:600, color:"#fff", width:"100%", minWidth:0 }}
        />
        {q && <button onClick={()=>{setQ("");setOpen(false);}} style={{ background:"none", border:"none", color:"rgba(255,255,255,.45)", fontSize:11, padding:0, lineHeight:1, flexShrink:0 }}>✕</button>}
      </div>
      {open && hits.length > 0 && (
        <div className="slide-down" style={{ position:"absolute", top:"calc(100% + 6px)", left:0, right:0, minWidth:220, background:th.paperSolid, borderRadius:12, border:`1px solid ${th.line}`, boxShadow:"0 12px 36px rgba(0,0,0,.18)", overflow:"hidden", zIndex:300 }}>
          {hits.map((s, i) => (
            <button key={s.sym} onClick={()=>go(s.sym)}
              style={{ display:"flex", alignItems:"center", gap:10, width:"100%", padding:"9px 12px", background:"none", border:"none", borderBottom:i<hits.length-1?`1px solid ${th.line}`:"none", textAlign:"left" }}
              onMouseEnter={e=>e.currentTarget.style.background="rgba(181,154,91,.09)"}
              onMouseLeave={e=>e.currentTarget.style.background="none"}>
              <div style={{ width:28, height:28, borderRadius:7, background:"rgba(181,154,91,.12)", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                <span style={{ fontSize:8, fontWeight:800, color:C.gold, fontFamily:"'IBM Plex Mono',monospace" }}>{s.sym.slice(0,3)}</span>
              </div>
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ fontSize:11, fontWeight:800, color:th.ink, fontFamily:"'IBM Plex Mono',monospace", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{s.sym}</div>
                <div style={{ fontSize:10, color:th.muted, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{s.name}</div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/* ─── SHELL ───────────────────────────────────────────────────── */
const NAV = [
  {to:"/",         l:"Accueil",    sub:null},
  {to:"/marketflow", l:"Market Flow", sub:null},
  {to:"/markets",  l:"Marché",     sub:[{to:"/markets/flux",    l:"Flux"},{to:"/markets/leaders",l:"Leaders"}]},
  {to:"/strategies",l:"Stratégies",sub:null},
  {to:"/screener", l:"Screener",   sub:null},
];

function Shell({ children, path, th, isDark, setDark }) {
  return (
    <div style={{ minHeight:"100vh", background:th.appBg }}>
      <InvelpsGlobalNav darkMode={isDark} onToggleDark={() => setDark((d) => !d)} />
      <MarketPageSubNav variant="page" />
      <div key={path} className="fade-up" style={{ position:"relative", zIndex:1 }}>
        {children}
      </div>
    </div>
  );
}

/* ─── ATOMS ───────────────────────────────────────────────────── */
function Bdg({ c="n", children, title }) {
  const m = {g:["rgba(111,138,91,.12)",C.green,"rgba(111,138,91,.28)"],r:["rgba(198,82,63,.12)",C.red,"rgba(198,82,63,.28)"],w:["rgba(202,168,94,.14)",C.amber,"rgba(202,168,94,.32)"],n:["rgba(181,154,91,.12)",C.gold,"rgba(181,154,91,.28)"]};
  const [bg,col,bd] = m[c]||m.n;
  return <span title={title} style={{ background:bg, color:col, border:`1px solid ${bd}`, padding:"3px 10px", borderRadius:999, fontSize:10, fontWeight:800, letterSpacing:.04, display:"inline-flex", alignItems:"center" }}>{children}</span>;
}

function SecHd({ icon, title, sub, badge, tooltip }) {
  return (
    <div style={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between", gap:10, marginBottom:14, flexWrap:"wrap" }}>
      <div style={{ display:"flex", alignItems:"flex-start", gap:10 }}>
        <div style={{ width:34, height:34, borderRadius:11, background:"rgba(181,154,91,.10)", border:"1px solid rgba(181,154,91,.20)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:16, color:C.gold, flexShrink:0 }}>{icon}</div>
        <div>
          <div style={{ display:"flex", alignItems:"center", gap:6 }}>
            <div style={{ fontSize:15, fontWeight:900, color:"var(--ink)", letterSpacing:"-.02em" }}>{title}</div>
            {tooltip && <InvelpsTipMark title={tooltip} down />}
          </div>
          {sub && <div style={{ fontSize:11, color:"var(--muted)", fontWeight:600, marginTop:1 }}>{sub}</div>}
        </div>
      </div>
      {badge}
    </div>
  );
}

/* ─── TOOLTIP (aligné TickerAnalysis : modal centré, × rouge, texte bas de casse) ─ */
function KpiTooltip({ text }) {
  const [open, setOpen] = useState(false);
  const isMobile = typeof window !== "undefined" ? window.matchMedia("(max-width: 820px)").matches : false;
  const tipText = String(text || "").trim().toLocaleLowerCase("fr-FR");
  if (!text) return null;
  return (
    <>
      <button
        type="button"
        title={text}
        onClick={(e) => {
          e.stopPropagation();
          setOpen(true);
        }}
        style={{
          background: "none",
          border: `1px solid ${C.gold}44`,
          borderRadius: 999,
          width: 15,
          height: 15,
          fontSize: 8,
          color: C.gold,
          cursor: "pointer",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          fontWeight: 800,
          flexShrink: 0,
          padding: 0,
        }}
      >
        i
      </button>
      {open ? (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 1400,
            background: "rgba(9,15,29,.38)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: isMobile ? "10px" : "14px",
          }}
          onClick={() => setOpen(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: isMobile ? "min(760px, calc(100vw - 20px))" : "min(760px, calc(100vw - 28px))",
              maxHeight: isMobile ? "78vh" : "min(82vh, 680px)",
              overflowY: "auto",
              borderRadius: 16,
              background: "var(--paper)",
              border: `1px solid ${C.gold}44`,
              boxShadow: "0 16px 36px rgba(0,0,0,.22)",
              padding: "12px 14px 16px",
              boxSizing: "border-box",
            }}
          >
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <button
                type="button"
                aria-label="Fermer"
                title="Fermer"
                onClick={() => setOpen(false)}
                style={{
                  background: "none",
                  border: "none",
                  color: C.red,
                  fontSize: 22,
                  lineHeight: 1,
                  cursor: "pointer",
                  padding: "0 4px",
                  fontWeight: 700,
                }}
              >
                ×
              </button>
            </div>
            <div
              style={{
                marginTop: 8,
                borderRadius: 12,
                border: "1px solid var(--line)",
                background: "var(--paper-alt)",
                padding: "12px 14px",
                color: "var(--ink2)",
                fontSize: 15,
                lineHeight: 1.5,
                overflowWrap: "anywhere",
                whiteSpace: "pre-wrap",
                textTransform: "lowercase",
              }}
            >
              {tipText}
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}

function Kpi({ label, value, sub, tone, tooltip }) {
  const col = tone==="g"?C.green:tone==="r"?C.red:tone==="w"?C.amber:C.gold;
  return (
    <div style={{ background:"var(--paper-alt)", border:"1px solid var(--line)", borderRadius:11, padding:"9px 11px" }}>
      <div style={{ display:"flex", alignItems:"center", gap:4, marginBottom:3 }}>
        <div style={{ fontSize:9, fontWeight:800, textTransform:"uppercase", letterSpacing:".13em", color:"var(--muted)" }}>{label}</div>
        {tooltip && <InvelpsTipMark title={tooltip} down />}
      </div>
      <div style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:16, fontWeight:800, color:col, lineHeight:1 }}>{value}</div>
      {sub && <div style={{ fontSize:10, color:"var(--muted)", marginTop:2, fontWeight:600 }}>{sub}</div>}
    </div>
  );
}

function SBar({ value }) {
  const c = sC(value);
  return (
    <div style={{ display:"flex", alignItems:"center", gap:6 }}>
      <div style={{ flex:1, height:5, borderRadius:999, background:"var(--line2)", overflow:"hidden" }}>
        <div style={{ height:"100%", width:`${Math.min(100,Math.max(0,value))}%`, background:c, borderRadius:999 }}/>
      </div>
      <span style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:11, fontWeight:800, color:c, width:26, textAlign:"right", flexShrink:0 }}>{(+value).toFixed(+value%1===0?0:1)}</span>
    </div>
  );
}

function SBdg({ tone, label }) {
  const m = {g:["rgba(111,138,91,.12)",C.green],r:["rgba(198,82,63,.12)",C.red],w:["rgba(202,168,94,.14)",C.amber],o:["rgba(210,138,82,.12)",C.orange]};
  const [bg,c] = m[tone]||m.w;
  return <span style={{ fontSize:10, fontWeight:800, padding:"2px 8px", borderRadius:6, background:bg, color:c, border:`1px solid ${c}33` }}>{label}</span>;
}

/* ═══════════════════════════════════════════════════════════════
   DATA — sp_Get_Homepage · sp_Front_homepage_cache
   Chaque bloc = 1 result set de sp_Get_Homepage.
   Les clés JS correspondent exactement aux alias SQL.
   ═══════════════════════════════════════════════════════════════ */

/* ── RS1 : Homepage_Cache ────────────────────────────────────────
   SELECT cache_key, cache_value FROM Homepage_Cache …
   Reconstruit en objet pivot côté front (comme le ferait l'API).
   ─────────────────────────────────────────────────────────────── */
const CACHE = {
  market_monthly_return : 6.82,          // ROUND(AVG(return_m1),2)
  dynamic_top_cnf       : 216,             // RS1 — sel_date 2026-04-30            // ROUND(MAX(cnf_pct),0)  Region
  dynamic_top_region    : "North America",   // axis_value ORDER BY cnf_pct DESC
  market_regime         : "ACCUMULATION", // flow_state modal Region
  top_strategy_name     : "Durable Moat",            // MAX FY 2026 = +92.85%
  top_strategy_fy       : 92.85,           // Durable Moat Analyse_year=2026          // ROUND(MAX(FY_Monthly),1)
  decision_score        : 50,             // ROUND(AVG(dd_global_score),0)
  buy_count             : 401,
  watch_count           : 6081,
  avoid_count           : 13516,
  // Tooltips dynamiques (générés par sp_Front_homepage_cache)
  tooltip_marche        : "Mois tres positif : les marches progressent fortement. Environnement favorable aux actions.",
  tooltip_dynamique        : "Flux massifs vers North America (+216 bp). Le CNF mesure l accumulation institutionnelle. Les grands fonds achetent massivement dans cette zone.",
  tooltip_regime        : "Phase ACCUMULATION : les grands investisseurs achetent discretement. Signal precurseur d une hausse potentielle. Bon moment pour identifier des opportunites de qualite.",
  tooltip_strategie        : '"Durable Moat" +92.85% FY (Jan-Avr 2026). Performance exceptionnelle sur 4 mois. Alpha genere : +74.85% vs benchmark. Les performances passees ne garantissent pas les resultats futurs.',
  tooltip_decision_engine        : "Score neutre : ni clairement haussier ni baissier. Approche prudente recommandee.",
  tooltip_buy_count        : "401 titres BUY (2.0% de l univers). Bon nombre d opportunites. Filtrez par region en accumulation et marge de securite.",
  tooltip_watch_count   : "Titres en surveillance : signal pas encore assez fort pour acheter ni vendre. À suivre pour un basculement vers BUY ou SELL.",
  tooltip_avoid_count   : "Titres avec signaux négatifs : au moins 3 critères sur 6 défavorables. Le modèle recommande d'éviter ou d'alléger ces positions.",
  tooltip_market_flow   : "Suit où vont les capitaux des grands institutionnels. CNF% = flux cumulé accumulé sur la période. Vert = entrées de capitaux, rouge = sorties.",
  tooltip_top_picks     : "Les 5 titres les mieux notés avec signal BUY. IScore = score global /100. MOS = décote par rapport à la valeur intrinsèque estimée. EVA = ROIC moins WACC.",
  tooltip_indices       : "Indices mondiaux. Signal Bullish = tendance haussière. vs DMA50 = écart à la moyenne 50 jours. vs DMA200 = écart à la moyenne 200 jours (long terme).",
  tooltip_leaders       : "Zones et secteurs qui attirent le plus de capitaux. LEADER CONFIRMED = flux entrants forts et persistants depuis plusieurs mois.",
  tooltip_strategies    : "Classement des approches quantitatives testées sur l'univers Invelps. FY Monthly = performance cumulée sur l'exercice.",
  tooltip_ou_investir   : "Synthèse automatique des signaux de la page. Ce résumé analytique oriente la réflexion — ce n'est pas un conseil d'investissement personnalisé.",
  buy_rate_pct          : 2.0,            // buy_count / total * 100 (calculé par SP)
  alpha_top_strategy    : 74.85,           // 92.85 - 18 (proxy MSCI World)          // top_strategy_fy − 18 (benchmark proxy)
  tooltip_buy_rate        : "BUY Rate 2.0% : marche tres selectif. Seuls les titres de tres haute conviction passent le filtre.",
  tooltip_alpha        : "Alpha genere par la meilleure strategie Invelps : +110.4% vs benchmark (proxy MSCI World 18%). Source : Index_Strategy, annee 2025.",
  // Générés par sp_Front_homepage_cache → tmp_tooltip_regions
  tooltip_region_NorthAsia        : "North Asia : flux neutres (-18 bp). Pas de tendance directionnelle claire.",
  tooltip_region_NorthAmerica        : "North America : flux massifs (+216 bp). Accumulation confirmee — les fonds achetent fortement. Zone la plus attractive du moment.",
  tooltip_region_SouthAmerica        : "South America : distribution (-32 bp). Les institutionnels vendent — evitez d augmenter votre exposition.",
  tooltip_region_SouthAsia        : "South Asia : flux neutres (18 bp). Pas de tendance directionnelle claire.",
  tooltip_region_Europe        : "Europe : flux positifs (+23 bp). Les capitaux entrent progressivement.",
  tooltip_region_SoutheastAsia        : "Southeast Asia : flux positifs (+38 bp). Les capitaux entrent progressivement.",
  tooltip_region_MiddleEast        : "Middle East : flux positifs (+107 bp). Les capitaux entrent progressivement.",
  // Tooltips dynamiques par secteur (tooltip_sector_<SecteurSansEspacesSansSlash>)
  // Générés par sp_Front_homepage_cache → tmp_tooltip_sectors
  tooltip_sector_Industrials        : "Industrials - ROTATION SUPPORT : des acheteurs reviennent sur ce secteur. Signal de rebond potentiel.",
  tooltip_sector_Utilities        : "Utilities - ROTATION SUPPORT : des acheteurs reviennent sur ce secteur. Signal de rebond potentiel.",
  tooltip_sector_Financials        : "Financials - NEUTRE : flux equilibres. Pas de tendance sectorielle dominante.",
  tooltip_sector_HealthCare        : "Health Care - ACCUMULATION : flux entrants persistants. Les institutionnels positionnent ce secteur.",
  tooltip_sector_Materials        : "Materials - NEUTRE : flux equilibres. Pas de tendance sectorielle dominante.",
  tooltip_sector_Energy        : "Energy - ROTATION SUPPORT : des acheteurs reviennent sur ce secteur. Signal de rebond potentiel.",
  tooltip_sector_InformationTechnology        : "Information Technology - ROTATION SUPPORT : des acheteurs reviennent sur ce secteur. Signal de rebond potentiel.",
  tooltip_sector_CommunicationServices        : "Communication Services - ACCUMULATION : flux entrants persistants. Les institutionnels positionnent ce secteur.",
  tooltip_sector_ConsumerStaples        : "Consumer Staples - ACCUMULATION : flux entrants persistants. Les institutionnels positionnent ce secteur.",
  tooltip_sector_ConsumerDiscretionary        : "Consumer Discretionary - ACCUMULATION : flux entrants persistants. Les institutionnels positionnent ce secteur.",
  tooltip_sector_RealEstate        : "Real Estate - ACCUMULATION : flux entrants persistants. Les institutionnels positionnent ce secteur.",
};

/* Helpers dérivés du cache */
/* Helper : clé tooltip région depuis CACHE (pattern tooltip_region_<SansEspaces>) */
const regionTooltip  = (region)  => CACHE[`tooltip_region_${region.replace(/ /g,"")}`] || "";
const sectorTooltip  = (sector)  => CACHE[`tooltip_sector_${sector.replace(/ /g,"").replace(/\//g,"")}`] || "";
const indexTooltip   = (symbol)  => CACHE[`tooltip_index_${symbol}`] || "";

/* Helper : formate CACHE.sel_date "2026-04-30" → "Avril 2026" */
const fDateFr = (d) => {
  if (!d) return "—";
  const [y, m] = d.split("-");
  const mois = ["","Janv.","Fév.","Mars","Avril","Mai","Juin",
                 "Juil.","Août","Sept.","Oct.","Nov.","Déc."];
  return `${mois[parseInt(m)]} ${y}`;
};
const BUY_RATE = ((CACHE.buy_count / (CACHE.buy_count + CACHE.watch_count + CACHE.avoid_count)) * 100).toFixed(1);
const REGIME_TONE = CACHE.market_regime === "ACCUMULATION" ? "g"
  : CACHE.market_regime === "DISTRIBUTION" ? "r" : "w";

/* ── RS2 : Capital_flow axis='Region' ────────────────────────────
   Colonnes SQL → JS :
     axis_value       → region
     cnf_pct          → cnf_pct
     flow_state       → flow_state
     flow_sens_score  → flow_sens_score
     wyckoff          → wyckoff
   ─────────────────────────────────────────────────────────────── */
const RFLOWS = [
  // Source: RS2 sp_Get_Homepage — Capital_flow axis='Region' — sel_date = MAX(sel_date)
  {region:"North America",  cnf_pct:215.57, flow_state:"ACCUMULATION",         flow_sens_score:"BULLISH", wyckoff:"ACCUMULATION"},
  {region:"Middle East",    cnf_pct:107.43, flow_state:"ACCUMULATION",         flow_sens_score:"BULLISH", wyckoff:"ACCUMULATION"},
  {region:"Southeast Asia", cnf_pct:38.42,  flow_state:"ACCUMULATION",         flow_sens_score:"BULLISH", wyckoff:"ACCUMULATION"},
  {region:"Europe",         cnf_pct:23.02,  flow_state:"ACCUMULATION",         flow_sens_score:"BULLISH", wyckoff:"ACCUMULATION"},
  {region:"South Asia",     cnf_pct:17.62,  flow_state:"NEUTRAL_VOLATILE",     flow_sens_score:"NEUTRAL", wyckoff:"TEST / ABSORPTION"},
  {region:"North Asia",     cnf_pct:-17.63, flow_state:"ROTATION_BUY_SUPPORT", flow_sens_score:"MIXED",   wyckoff:"RE-ACCUMULATION (support)"},
  {region:"South America",  cnf_pct:-31.87, flow_state:"DISTRIBUTION",         flow_sens_score:"BEARISH", wyckoff:"DISTRIBUTION"},
];

/* ── RS3 : Capital_flow axis='Sector' ────────────────────────────
   Colonnes SQL → JS :
     sector     → sector
     ret        → ret         (return_m1 depuis Momentum_return)
     cnf_pct    → cnf_pct
     size       → size        (n_constituents)
     flow_state → flow_state
   // Source: RS3 sp_Get_Homepage — Capital_flow axis='Sector' — sel_date = MAX(sel_date)
   ─────────────────────────────────────────────────────────────── */
// Sector Rotation — Source: RS3 sp_Get_Homepage — Capital_flow axis='Sector'
// cnf_pct = FCN (Cumulative Net Flow) | pnf = PNF (Pressure Net Flow)
// Valeurs réelles depuis invelps.com page Marché (2026-04-30)
const SECTORS_D = [
  {sector:"Real Estate",            name:"Real Estate",    cnf_pct:119.48, pnf:70.93,  flow_state:"ACCUMULATION",         ret:10.70, size:12},
  {sector:"Consumer Discretionary", name:"Consumer Disc.", cnf_pct:108.23, pnf:69.75,  flow_state:"ACCUMULATION",         ret:5.92,  size:14},
  {sector:"Health Care",            name:"Health",         cnf_pct:92.83,  pnf:17.86,  flow_state:"ACCUMULATION",         ret:0.34,  size:7},
  {sector:"Consumer Staples",       name:"Cons. Staples",  cnf_pct:91.34,  pnf:100.00, flow_state:"ACCUMULATION",         ret:-3.25, size:6},
  {sector:"Communication Services", name:"Comm. Services", cnf_pct:31.82,  pnf:50.05,  flow_state:"ACCUMULATION",         ret:4.64,  size:8},
  {sector:"Materials",              name:"Materials",      cnf_pct:10.75,  pnf:68.82,  flow_state:"NEUTRAL_VOLATILE",     ret:3.79,  size:27},
  {sector:"Financials",             name:"Financials",     cnf_pct:0.10,   pnf:25.81,  flow_state:"NEUTRAL_VOLATILE",     ret:15.94, size:22},
  {sector:"Industrials",            name:"Industrials",    cnf_pct:-9.67,  pnf:29.14,  flow_state:"ROTATION_BUY_SUPPORT", ret:5.70,  size:43},
  {sector:"Information Technology", name:"Info. Tech.",    cnf_pct:-10.61, pnf:63.24,  flow_state:"ROTATION_BUY_SUPPORT", ret:25.29, size:28},
  {sector:"Utilities",              name:"Utilities",      cnf_pct:-15.77, pnf:26.82,  flow_state:"ROTATION_BUY_SUPPORT", ret:5.35,  size:13},
  {sector:"Energy",                 name:"Energy",         cnf_pct:-25.04, pnf:null,   flow_state:"ROTATION_BUY_SUPPORT", ret:0.57,  size:20},
];
// Alias courts pour le scatter (le composant utilise .name)
const SECTORS_D_VIEW = SECTORS_D.map(d => ({
  ...d,
  name: d.sector
    .replace("Information Technology","Info. Tech.")
    .replace("Communication Services","Comm. Services")
    .replace("Consumer Staples","Cons. Staples")
    .replace("Consumer Discretionary","Consumer Disc.")
    .replace("Health Care","Health"),
  pnf: d.pnf,   // PNF réel depuis RS3 nfp_pct
  ret: d.ret,
}));

/* ── RS4 : Momentum_return group_dim='Market' ────────────────────
   Colonnes SQL → JS :
     symbol      → sym      (group_value = code pays ISO2)
     return_m1   → ret      (ROUND(return_m1,2))
     signal      → sig      (flow_state → traduit en Bullish/Bearish/Neutral)
     signal_50   → s50      (CASE persistence_3m)
     signal_200  → s200     (CASE persistence_6m)
   Enrichi manuellement avec reg/ctry/name (non fournis par la SP,
   à injecter via une table de référence côté API).
   ─────────────────────────────────────────────────────────────── */
const INDICES_D = [
  // Source: RS4 sp_Get_Homepage — Momentum_return group_dim='Market' (Gh4 — 2026-04-30)
  // sig = flow_state depuis Momentum_return | vs50/vs200 depuis MarketTrend (2026-04-24)
  // ret = return_m1 | trié par ret DESC
  {sym:"JP", reg:"Asia",        ctry:"JP", name:"Nikkei 225",    sig:"OUTFLOW",          s50:"Bearish", s200:"Bearish", vs50:-2.9,  vs200:10.8,  ret:17.38},
  {sym:"KR", reg:"Asia",        ctry:"KR", name:"KOSPI",          sig:"OUTFLOW",          s50:"Bearish", s200:"Bearish", vs50:null,  vs200:null,  ret:15.97},
  {sym:"GB", reg:"Europe",      ctry:"GB", name:"FTSE 100",       sig:"OUTFLOW",          s50:"Bearish", s200:"Bearish", vs50:null,  vs200:null,  ret:12.13},
  {sym:"DE", reg:"Europe",      ctry:"DE", name:"DAX",            sig:"OUTFLOW",          s50:"Bearish", s200:"Bearish", vs50:null,  vs200:null,  ret:11.28},
  {sym:"CN", reg:"Asia",        ctry:"CN", name:"SSE Composite",  sig:"OUTFLOW",          s50:"Bearish", s200:"Bearish", vs50:-4.6,  vs200:0.7,   ret:11.03},
  {sym:"AU", reg:"Asia",        ctry:"AU", name:"ASX 200",        sig:"OUTFLOW",          s50:"Bearish", s200:"Bearish", vs50:null,  vs200:null,  ret:11.03},
  {sym:"IN", reg:"Asia",        ctry:"IN", name:"Nifty 50",       sig:"LEADER_CONFIRMED", s50:"Bullish", s200:"Bullish", vs50:-7.9,  vs200:-9.9,  ret:9.40},
  {sym:"AE", reg:"Middle East", ctry:"AE", name:"Tadawul",        sig:"OUTFLOW",          s50:"Bearish", s200:"Bearish", vs50:6.3,   vs200:1.2,   ret:7.94},
  {sym:"AR", reg:"Americas",    ctry:"AR", name:"Merval",          sig:"OUTFLOW",          s50:"Bearish", s200:"Bearish", vs50:4.9,   vs200:19.5,  ret:5.88},
  {sym:"FR", reg:"Europe",      ctry:"FR", name:"CAC 40",         sig:"OUTFLOW",          s50:"Bearish", s200:"Bearish", vs50:null,  vs200:null,  ret:5.86},
  {sym:"HK", reg:"Asia",        ctry:"HK", name:"FTSE China A50", sig:"OUTFLOW",          s50:"Bearish", s200:"Bearish", vs50:-1.8,  vs200:-1.0,  ret:4.10},
  {sym:"US", reg:"Americas",    ctry:"US", name:"S&P 500",        sig:"OUTFLOW",          s50:"Bearish", s200:"Bearish", vs50:-3.0,  vs200:-0.9,  ret:1.56},
  {sym:"BR", reg:"Americas",    ctry:"BR", name:"Bovespa",        sig:"OUTFLOW",          s50:"Bearish", s200:"Bearish", vs50:null,  vs200:null,  ret:0.97},
  {sym:"CL", reg:"Americas",    ctry:"CL", name:"IPSA",           sig:"OUTFLOW",          s50:"Bearish", s200:"Bearish", vs50:null,  vs200:null,  ret:0.03},
  {sym:"IT", reg:"Europe",      ctry:"IT", name:"FTSE MIB",       sig:"OUTFLOW",          s50:"Bullish", s200:"Bullish", vs50:0.8,   vs200:5.6,   ret:null},
];

/* ── RS5 : Momentum_Top20 (Leadership Board) ─────────────────────
   Colonnes SQL → JS :
     scope            → scope          (group_dim)
     name             → name           (Region)
     leader_symbol    → leader_symbol  (symbol)
     ret_m1           → ret_m1         (return_m1)
     flow_state       → flow_state
     leadership_rank  → leadership_rank(flow_state_rank)
     persistence      → persistence    (persistence_6m)
   ─────────────────────────────────────────────────────────────── */
// Leadership Board — RS5 : 1 leader par axe depuis Momentum_return + Momentum_Top20
// Colonnes RS5 : scope, name, leader_symbol, ret_m1, flow_state,
//               leadership_rank, persistence, market_iso2, sector, mkt_class, style
const LEADERS_D = [
  { rank:1, scope:"Region",    name:"South Asia",             leader_symbol:"001510.KS", ret_m1:16.56,  flow_state:"OUTFLOW",  persistence:0, market_iso2:"PK", sector:"Financials",            mkt_class:"Mega Cap", style:"Core"   },
  { rank:2, scope:"Market",    name:"BH",                     leader_symbol:"EXICOM.BO", ret_m1:44.05,  flow_state:"OUTFLOW",  persistence:0, market_iso2:"BH", sector:"Industrials",           mkt_class:"Mega Cap", style:"Growth" },
  { rank:3, scope:"Sector",    name:"Information Technology", leader_symbol:"SIVE.ST",   ret_m1:25.29,  flow_state:"OUTFLOW",  persistence:0, market_iso2:"SE", sector:"Information Technology", mkt_class:"Mid Cap",  style:"Growth" },
  { rank:4, scope:"Mkt_Class", name:"Mega Cap",               leader_symbol:"047040.KS", ret_m1:15.90,  flow_state:"OUTFLOW",  persistence:0, market_iso2:"PK", sector:"Industrials",           mkt_class:"Mega Cap", style:"Value"  },
  { rank:5, scope:"Style",     name:"Income",                 leader_symbol:"003018.SZ", ret_m1:22.08,  flow_state:"OUTFLOW",  persistence:0, market_iso2:"CN", sector:"Consumer Discretionary", mkt_class:"Large Cap",style:"Income" },
];

/* ── RS6 : StockDashboard_Decisions (Decision Engine) ───────────
   Colonnes SQL → JS :
     decision_score → CACHE.decision_score
     bias           → CACHE.market_regime (proxy)
     buy_count      → CACHE.buy_count
     watch_count    → CACHE.watch_count
     avoid_count    → CACHE.avoid_count
   (Données déjà dans CACHE ci-dessus — pas de tableau séparé)
   ─────────────────────────────────────────────────────────────── */

/* ── RS7 : StockDashboard_Decisions JOIN StockDashboard (Top Picks)
   Colonnes SQL → JS :
     symbol        → sym
     company_name  → name
     signal        → sig
     iscore        → is
     fund_score    → fs
     trend_score   → tr
     mos_pct       → mos        (Margin_of_safety * 100)
     roic_vs_wacc  → roic_vs_wacc
     global_score  → global_score
   ─────────────────────────────────────────────────────────────── */
const OPPS_D = [
  // Source: RS7 sp_Get_Homepage — StockDashboard_Decisions JOIN StockDashboard — last_updated 2026-04-09
  // mos = Margin_of_safety stocké en % → pas de *100 (9432/100=94.32 → utiliser 94.3)
  // roic_vs_wacc = ratio décimal brut depuis SP (0.28 = 28pts de spread ROIC-WACC)
  {sym:"NVDA",         name:"NVIDIA Corporation",            sig:"BUY", is:78.04, fs:93.76, tr:56.81, mos:24.9,  roic_vs_wacc:0.45, global_score:92.0,
   these:"Monopole GPU IA. Datacenter en accélération structurelle. ROIC-WACC +45pts. MOS 24.9%."},
  {sym:"ONEGLOBAL.BO", name:"One Global Service Provider",   sig:"BUY", is:75.87, fs:91.68, tr:70.09, mos:94.3,  roic_vs_wacc:0.28, global_score:84.0,
   these:"Leader BPO Asie du Sud-Est. Croissance >20%/an. ROIC-WACC +28pts. MOS 94.3%."},
  {sym:"CMCX.L",       name:"CMC Markets plc",               sig:"BUY", is:75.37, fs:93.24, tr:63.44, mos:55.2,  roic_vs_wacc:0.14, global_score:92.0,
   these:"Plateforme CFD profitable. P/E 7x, dividende 8%. ROIC-WACC +14pts. MOS 55.2%."},
  {sym:"NICL.JK",      name:"PT PAM Mineral Tbk",            sig:"BUY", is:75.02, fs:89.70, tr:71.33, mos:24.6,  roic_vs_wacc:0.39, global_score:84.0,
   these:"Nickel indonésien chaîne EV. Réserves longue durée. ROIC-WACC +39pts. MOS 24.6%."},
  {sym:"SMDR.JK",      name:"PT Samudera Indonesia Tbk",     sig:"BUY", is:74.79, fs:92.17, tr:64.29, mos:999,   roic_vs_wacc:0.03, global_score:84.0,
   these:"Armateur reshoring Asie du Sud-Est. P/B 0.4x. ROIC-WACC +3pts. MOS exceptionnel."},
  {sym:"BLTA.JK",      name:"PT Berlian Laju Tanker Tbk",    sig:"BUY", is:74.57, fs:89.31, tr:66.40, mos:97.9,  roic_vs_wacc:0.01, global_score:84.0,
   these:"Tanker chimique. Cycle renouvellement flotte mondiale. MOS 97.9%."},
  {sym:"7191.T",       name:"Entrust Inc.",                  sig:"BUY", is:74.24, fs:92.06, tr:60.75, mos:63.9,  roic_vs_wacc:0.25, global_score:84.0,
   these:"Fintech japonaise. Momentum fort. ROIC-WACC +25pts. MOS 63.9%."},
  {sym:"NELY.JK",      name:"PT Pelayaran Nelly Dwi Putri",  sig:"BUY", is:74.22, fs:92.21, tr:54.13, mos:71.4,  roic_vs_wacc:0.18, global_score:84.0,
   these:"Transport maritime indonésien. Wide moat confirmé. ROIC-WACC +18pts. MOS 71.4%."},
];

/* ── RS8 : Index_Strategy (Top Stratégies) ───────────────────────
   Colonnes SQL → JS :
     strategy    → s
     fy_monthly  → fy          (ROUND(FY_Monthly,2))
     cagr_5y     → cagr_5y    (ROUND(FYA_CAGR_5Y,2))
     sharpe_1y   → sharpe_1y  (ROUND(Sharpe_ratio_1Y,2))
     Analyse_year → year
   ─────────────────────────────────────────────────────────────── */
const STRATS_D = [
  // Source: RS8 sp_Get_Homepage — Index_Strategy Analyse_year=2026 (MAX)
  // Données partielles : M1-M4 (Jan-Avr 2026) — FY = YTD 4 mois
  // Sharpe_ratio_1Y et Annual_mdd_1Y non encore calculés pour 2026
  {s:"Durable Moat",                      a:"Dorsey",          fy:92.85, cagr_5y:-1.36, sharpe_1y:null, mdd_1y:null},
  {s:"Deep Value",                         a:"Graham",          fy:46.86, cagr_5y:9.60,  sharpe_1y:null, mdd_1y:null},
  {s:"Tiny Titans",                        a:"Carlisle",        fy:44.45, cagr_5y:null,  sharpe_1y:null, mdd_1y:null},
  {s:"Rule 1",                             a:"Town",            fy:39.50, cagr_5y:15.80, sharpe_1y:null, mdd_1y:null},
  {s:"Market Leaders Low P/CF",            a:"Quant Value",     fy:26.95, cagr_5y:14.56, sharpe_1y:null, mdd_1y:null},
  {s:"Piotroski F-Score",                  a:"Piotroski",       fy:12.64, cagr_5y:6.75,  sharpe_1y:null, mdd_1y:null},
  {s:"Capital Compounders",                a:"Invelps",         fy:11.65, cagr_5y:null,  sharpe_1y:null, mdd_1y:null},
  {s:"Value Investing Toolbox - Value",    a:"Value Toolbox",   fy:10.03, cagr_5y:19.00, sharpe_1y:null, mdd_1y:null},
  {s:"100 to 1",                           a:"Mayer",           fy:9.93,  cagr_5y:-7.66, sharpe_1y:null, mdd_1y:null},
  {s:"Value Investing Toolbox - Core",     a:"Value Toolbox",   fy:9.66,  cagr_5y:10.45, sharpe_1y:null, mdd_1y:null},
  {s:"Small Cap Value & Growth",           a:"Quantitative",    fy:9.25,  cagr_5y:-1.99, sharpe_1y:null, mdd_1y:null},
  {s:"The Intelligent Investor - Defensive",a:"Graham",         fy:8.70,  cagr_5y:8.81,  sharpe_1y:null, mdd_1y:null},
];

/* Performance cumulée top strat (pour le chart StrategyCard) */
const STRAT_CURVE = [
  // Source: RS8 — Index_Strategy Analyse_year=2026 — top strat: Durable Moat
  // M1=Jan M2=Fév M3=Mar M4=Avr — données partielles (4 mois)
  // s = perf cumulée géométrique Durable Moat | b = SPX cumulatif géométrique
  // SPX 2024 = 23.31% → mensuel = 1.7614%/mois
  {m:"Jan", s:38.91, b:1.76},
  {m:"Fév", s:37.77, b:3.55},
  {m:"Mar", s:22.63, b:5.38},
  {m:"Avr", s:92.85, b:7.23},
];

/* Market Pulse — construit depuis RS2 (proxy 6m glissant)
   Colonnes : m=mois · v=CNF Invelps · b=benchmark MSCI World (proxy) · p=PNF delta mensuel */
const PULSE = [
  // Source: RS10 sp_Get_Homepage — Capital_flow axis='Region' (2026-04-30)
  // v = cnf_pct | p = nfp_pct | b = SPX proxy mensuel (annual_return_tr 2024 / 12 × 100)
  // SPX annual 2024 = 23.31% → proxy mensuel = 1.76%
  {region:"North America",  v:215.57, b:1.76, p:1.07},
  {region:"Middle East",    v:107.43, b:1.76, p:100.00},
  {region:"Southeast Asia", v:38.42,  b:1.76, p:79.46},
  {region:"Europe",         v:23.02,  b:1.76, p:18.67},
  {region:"South Asia",     v:17.62,  b:1.76, p:100.00},
  {region:"North Asia",     v:-17.63, b:1.76, p:27.16},
  {region:"South America",  v:-31.87, b:1.76, p:-100.00},
];

const HomeBundleContext = createContext(null);

function getStaticHomeBundle() {
  return {
    CACHE,
    RFLOWS,
    SECTORS_D,
    SECTORS_D_VIEW,
    INDICES_D,
    LEADERS_D,
    OPPS_D,
    STRATS_D,
    STRAT_CURVE,
    PULSE,
    regionTooltip,
    sectorTooltip,
    indexTooltip,
    fDateFr,
    BUY_RATE,
    REGIME_TONE,
  };
}

function useHomeBundle() {
  return useContext(HomeBundleContext) || getStaticHomeBundle();
}

/* ─── HOME ────────────────────────────────────────────────────── */
function HomePage({ nav, th, openTicker }) {
  const {
    CACHE,
    RFLOWS,
    SECTORS_D,
    LEADERS_D,
    OPPS_D,
    PULSE,
    fDateFr,
    REGIME_TONE,
  } = useHomeBundle();
  return (
    <main className="page">

      {/* ── HERO BLOCK — fond vert foncé ── */}
      <div style={{ borderRadius:24, background:"linear-gradient(135deg,#2a4f3f 0%,#1e3a2e 60%,#162d24 100%)", padding:"clamp(24px,4vw,40px)", position:"relative", overflow:"hidden" }}>
        {/* Texture overlay */}
        <div style={{ position:"absolute", inset:0, opacity:.04, backgroundImage:"radial-gradient(circle at 1px 1px, #fff 1px, transparent 0)", backgroundSize:"24px 24px", pointerEvents:"none" }}/>
        {/* 2-col layout on desktop */}
        <div style={{ display:"flex", gap:32, alignItems:"center", flexWrap:"wrap" }}>
          {/* Left: text + CTAs */}
          <div style={{ flex:"1 1 320px", minWidth:0 }}>
            {/* Badge */}
            <div style={{ display:"inline-flex", alignItems:"center", borderRadius:999, border:"1px solid rgba(181,154,91,.4)", background:"rgba(181,154,91,.12)", padding:"3px 12px", marginBottom:18 }}>
              <span style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:9, fontWeight:700, letterSpacing:".18em", textTransform:"uppercase", color:C.gold }}>Invelps Score Engine</span>
            </div>
            {/* Headline */}
            <h1 style={{ fontFamily:"'Cormorant Garamond',Georgia,serif", fontSize:"clamp(28px,3.8vw,52px)", fontWeight:700, color:"#fff", lineHeight:1.08, letterSpacing:"-.02em", marginBottom:14 }}>
              Décider avec structure.<br/>Investir avec discipline.
            </h1>
            <p style={{ fontSize:13, color:"rgba(255,255,255,.62)", fontWeight:500, lineHeight:1.6, marginBottom:24, maxWidth:480 }}>
              Une lecture top-down du marché combinée à une analyse fondamentale, technique et de valorisation pour isoler les opportunités réellement exploitables.
            </p>
            {/* CTAs */}
            <div style={{ display:"flex", gap:10, flexWrap:"wrap" }}>
              <button onClick={()=>nav("/markets")} style={{ padding:"11px 22px", borderRadius:12, border:"none", background:"linear-gradient(135deg,#caa85e,#b59a5b)", color:"#fff", fontSize:13, fontWeight:800, cursor:"pointer", letterSpacing:"-.01em" }}>
                Explorer les marchés
              </button>
              <button onClick={()=>nav("/screener")} style={{ padding:"11px 22px", borderRadius:12, border:"1px solid rgba(255,255,255,.22)", background:"rgba(255,255,255,.08)", color:"rgba(255,255,255,.88)", fontSize:13, fontWeight:800, cursor:"pointer" }}>
                Ouvrir le screener
              </button>
              <button onClick={()=>nav("/strategies")} style={{ padding:"11px 22px", borderRadius:12, border:"1px solid rgba(181,154,91,.35)", background:"rgba(181,154,91,.08)", color:C.gold, fontSize:13, fontWeight:800, cursor:"pointer" }}>
                Découvrir les stratégies
              </button>
            </div>
          </div>
          {/* Right: 4 stats 2×2 */}
          <div style={{ flex:"0 0 auto", width:"clamp(260px,35%,420px)", display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
            {[
              {l:"BUY Rate",       v:`${CACHE.buy_rate_pct}%`,             sub:`${CACHE.buy_count.toLocaleString('fr-FR')} / ${(CACHE.buy_count+CACHE.watch_count+CACHE.avoid_count).toLocaleString('fr-FR')} titres`, c:C.green, tip:CACHE.tooltip_buy_rate},
              {l:"Flux dominant",  v:`+${CACHE.dynamic_top_cnf} bp`,       sub:`${CACHE.dynamic_top_region} · Accum.`,  c:C.amber, tip:CACHE.tooltip_dynamique},
              {l:"Alpha top str.", v:`+${CACHE.alpha_top_strategy}%`,      sub:"vs benchmark FY",                        c:C.green, tip:CACHE.tooltip_alpha},
              {l:"Momentum net",   v:"Risk selective",                      sub:"CNF pondéré univers",                    c:C.amber, tip:CACHE.tooltip_regime},
            ].map(({l,v,sub,c,tip})=>(
              <div key={l} style={{ borderRadius:12, border:"1px solid rgba(255,255,255,.1)", background:"rgba(255,255,255,.07)", padding:"14px 16px", position:"relative" }}>
                <div style={{ fontSize:9, fontWeight:800, textTransform:"uppercase", letterSpacing:".12em", color:"rgba(255,255,255,.4)", marginBottom:6 }}>{l}</div>
                <div style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:18, fontWeight:800, color:c, lineHeight:1, marginBottom:4 }}>{v}</div>
                <div style={{ fontSize:9, color:"rgba(255,255,255,.38)", fontWeight:600 }}>{sub}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── MARKET ENGINE + PULSE + DECISION ENGINE ── */}
      <div className="card">
        <div className="card-pad">
          {/* Header global */}
          <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:18, paddingBottom:14, borderBottom:"1px solid var(--line2)" }}>
            <span style={{ fontSize:16 }}>⊛</span>
            <div>
              <div style={{ fontSize:14, fontWeight:800, color:"var(--ink)", letterSpacing:"-.01em" }}>Market Engine</div>
              <div style={{ fontSize:11, color:"var(--muted)", fontWeight:600, marginTop:1 }}>Régime · Flux · Score univers complet</div>
            </div>
            <div style={{ marginLeft:"auto", display:"flex", gap:6, flexWrap:"wrap" }}>
              <Bdg c="r">{CACHE.market_regime}</Bdg>
              <Bdg>{fDateFr(CACHE.sel_date)}</Bdg>
            </div>
          </div>

          {/* 3 zones horizontales */}
          <div style={{ display:"grid", gridTemplateColumns:"1fr", gap:16 }} className="engine-grid">
            {/* Ligne 1 : KPIs régime — alimentés depuis RS1 Homepage_Cache */}
            <div className="metric-grid">
              <Kpi label="Marché" value={`${CACHE.market_monthly_return > 0 ? "+" : ""}${CACHE.market_monthly_return}%`} sub={fDateFr(CACHE.sel_date)} tone={CACHE.market_monthly_return >= 0 ? "g" : "r"}
                tooltip={CACHE.tooltip_marche}/>
              <Kpi label="Dynamique" value={`+${CACHE.dynamic_top_cnf} bp`} sub={`${CACHE.dynamic_top_region} flux`} tone="g"
                tooltip={CACHE.tooltip_dynamique}/>
              <Kpi label="Stratégie" value={`+${CACHE.top_strategy_fy}%`} sub={CACHE.top_strategy_name} tone="g"
                tooltip={CACHE.tooltip_strategie}/>
              <Kpi label="Régime" value={CACHE.market_regime === "DISTRIBUTION" ? "Distrib." : CACHE.market_regime === "ACCUMULATION" ? "Accum." : "Rotation"} sub={CACHE.market_regime} tone={REGIME_TONE}
                tooltip={CACHE.tooltip_regime}/>
            </div>

            {/* Ligne 2 : Pulse + Decision Engine côte à côte */}
            <div style={{ display:"grid", gridTemplateColumns:"1fr", gap:14 }} className="pulse-de-grid">
              {/* Market Pulse */}
              <div style={{ borderRadius:14, border:"1px solid var(--line)", padding:14, background:"var(--paper-alt)" }}>
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:10 }}>
                  <div>
                    <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                      <div style={{ fontSize:10, fontWeight:800, textTransform:"uppercase", letterSpacing:".16em", color:C.gold }}>Market Pulse</div>
                      <InvelpsTipMark title={CACHE.tooltip_pulse}/>
                    </div>
                    <div style={{ fontSize:11, color:"var(--muted)", fontWeight:600, marginTop:2 }}>CNF Invelps · Benchmark · Delta PNF mensuel</div>
                  </div>
                  <div style={{ display:"flex", gap:6, flexWrap:"wrap", justifyContent:"flex-end" }}>
                    <Bdg c="w">395 bp</Bdg>
                    <Bdg c="g">+10.1% α</Bdg>
                  </div>
                </div>
                {/* Légende */}
                <div style={{ display:"flex", gap:12, marginBottom:8, flexWrap:"wrap" }}>
                  {[
                    {col:C.green,  label:"CNF Invelps", dash:false},
                    {col:"var(--muted)", label:"Benchmark MSCI", dash:true},
                    {col:"#6699cc44", label:"PNF mensuel", dash:false, bar:true},
                  ].map(({col,label,dash,bar})=>(
                    <div key={label} style={{ display:"flex", alignItems:"center", gap:4 }}>
                      {bar
                        ? <div style={{ width:10, height:10, borderRadius:2, background:"#6699cc66" }}/>
                        : <div style={{ width:18, height:2, background:col, borderRadius:1, ...(dash?{backgroundImage:`repeating-linear-gradient(90deg,${col} 0,${col} 4px,transparent 4px,transparent 7px)`,background:"transparent"}:{}) }}/>
                      }
                      <span style={{ fontSize:9, color:"var(--muted)", fontWeight:600 }}>{label}</span>
                    </div>
                  ))}
                </div>
                <div style={{ height:185 }}>
                  <ResponsiveContainer width="100%" height={185}>
                    <AreaChart data={PULSE} margin={{top:6,left:-22,right:6,bottom:28}}>
                      <defs>
                        <linearGradient id="pg" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor={C.green} stopOpacity={.22}/>
                          <stop offset="95%" stopColor={C.green} stopOpacity={.02}/>
                        </linearGradient>
                        <linearGradient id="pb" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#6699cc" stopOpacity={.28}/>
                          <stop offset="95%" stopColor="#6699cc" stopOpacity={.04}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid stroke="var(--line2)" vertical={false}/>
                      <ReferenceLine y={0} stroke="var(--line)" strokeDasharray="3 3"/>
                      <XAxis dataKey="region"
                        tick={{fontSize:8,fill:"var(--muted)",fontWeight:700}}
                        tickLine={false} axisLine={false}
                        interval={0} angle={-25} textAnchor="end" height={36}
                        tickFormatter={v=>v.replace(" ","\u00A0").slice(0,10)}/>
                      <YAxis hide domain={['auto','auto']}/>
                      <Tooltip
                        contentStyle={{borderRadius:10,border:"1px solid var(--line)",background:th.paperSolid,color:"var(--ink)",fontSize:11}}
                        formatter={(val,name) => {
                          if (name==="v") return [`${val>0?"+":""}${(+val).toFixed(1)} bp`, "CNF Invelps"];
                          if (name==="b") return [`${val>0?"+":""}${(+val).toFixed(2)}%`, "SPX benchmark"];
                          if (name==="p") return [`${val>0?"+":""}${(+val).toFixed(1)}%`, "PNF delta"];
                          return [val, name];
                        }}
                        labelFormatter={v=>v}
                      />
                      <Area type="monotone" dataKey="p" stroke="#6699cc" strokeWidth={0} fill="url(#pb)" opacity={.7}/>
                      <Area type="monotone" dataKey="b" stroke="var(--muted)" strokeWidth={1.5} strokeDasharray="5 4" fill="none"/>
                      <Area type="monotone" dataKey="v" stroke={C.green} strokeWidth={2.5} fill="url(#pg)"/>
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
                {/* Mini métriques sous le chart */}
                <div style={{ display:"flex", gap:8, marginTop:10 }}>
                  {[
                    {l:"CNF max", v:"+395 bp", c:C.green},
                    {l:"CNF min", v:"−37 bp",  c:C.red},
                    {l:"vs Bench Δ", v:"+367 bp", c:C.green},
                    {l:"Mois Δ",  v:"+432 bp", c:C.green},
                  ].map(({l,v,c})=>(
                    <div key={l} style={{ flex:1, borderRadius:8, border:"1px solid var(--line2)", padding:"5px 8px", background:"var(--paper)" }}>
                      <div style={{ fontSize:8, fontWeight:800, textTransform:"uppercase", letterSpacing:".09em", color:"var(--muted)", marginBottom:2 }}>{l}</div>
                      <div style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:12, fontWeight:800, color:c, lineHeight:1 }}>{v}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Decision Engine */}
              <div style={{ borderRadius:14, border:"1px solid var(--line)", padding:14, background:"var(--paper-alt)" }}>
                <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:14 }}>
                  <div style={{ fontSize:10, fontWeight:800, textTransform:"uppercase", letterSpacing:".16em", color:C.gold }}>Decision Engine</div>
                  <InvelpsTipMark title={CACHE.tooltip_decision_engine}/>
                  <span style={{ marginLeft:"auto" }}><Bdg c={CACHE.decision_score>=55?"g":CACHE.decision_score>=40?"w":"r"}>{CACHE.decision_score>=55?"HAUSSIER":CACHE.decision_score>=40?"NEUTRE":"BAISSIER"}</Bdg></span>
                </div>
                {/* Doughnut + chips côte à côte */}
                <div style={{ display:"flex", alignItems:"center", gap:16 }}>
                  {/* Doughnut */}
                  <div style={{ position:"relative", width:110, height:110, flexShrink:0 }}>
                    <svg width="110" height="110" viewBox="0 0 110 110" style={{ position:"absolute" }}>
                      <circle cx="55" cy="55" r="46" fill="none" stroke="var(--line2)" strokeWidth="10"/>
                      <circle cx="55" cy="55" r="46" fill="none" stroke={C.amber} strokeWidth="10" strokeLinecap="round"
                        strokeDasharray={`${2*Math.PI*46*.50} ${2*Math.PI*46*.50}`} strokeDashoffset={2*Math.PI*46*.25} transform="rotate(-90 55 55)"/>
                    </svg>
                    <div style={{ position:"absolute", inset:0, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center" }}>
                      <span style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:26, fontWeight:800, color:C.amber, lineHeight:1 }}>50</span>
                      <span style={{ fontSize:8, fontWeight:800, textTransform:"uppercase", letterSpacing:".13em", color:"var(--muted)", marginTop:2 }}>/100</span>
                    </div>
                  </div>
                {/* Chips à droite — RS6 StockDashboard_Decisions */}
                  <div style={{ flex:1, display:"flex", flexDirection:"column", gap:8 }}>
                    {[
                      {l:"BUY",   v:CACHE.buy_count.toLocaleString("fr-FR"),   sub:"titres", tone:"g", tip:CACHE.tooltip_buy_count},
                      {l:"Watch", v:CACHE.watch_count.toLocaleString("fr-FR"),  sub:"titres", tone:"w", tip:CACHE.tooltip_watch_count},
                      {l:"Avoid", v:CACHE.avoid_count.toLocaleString("fr-FR"),  sub:"titres", tone:"r", tip:CACHE.tooltip_avoid_count},
                    ].map(({l,v,sub,tone,tip})=>{
                      const col = tone==="g"?C.green:tone==="r"?C.red:C.amber;
                      return (
                        <div key={l} style={{ display:"flex", alignItems:"center", gap:10, borderRadius:9, border:`1px solid ${col}28`, background:`${col}0d`, padding:"7px 12px" }}>
                          <div style={{ width:6, height:6, borderRadius:999, background:col, flexShrink:0 }}/>
                          <div style={{ flex:1 }}>
                            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"baseline" }}>
                              <span style={{ fontSize:10, fontWeight:800, color:"var(--muted)", textTransform:"uppercase", letterSpacing:".08em" }}>{l}</span>
                              <span style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:15, fontWeight:800, color:col }}>{v}</span>
                            </div>
                            <div style={{ fontSize:9, color:"var(--muted)", fontWeight:600 }}>{sub}</div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── OÙ INVESTIR — synthèse top-down ── */}
      <div className="card">
        <div className="card-pad">
          <SecHd icon="◈" title="Où investir ?" sub="Synthèse top-down : flux · leadership · valorisation" badge={<Bdg c="g">Analyse</Bdg>} tooltip={CACHE.tooltip_ou_investir}/>
          <div className="synth-grid">
            {[
              {t:"Zones à privilégier", dot:C.green, items:[
                `${CACHE.dynamic_top_region} : flux dominant +${CACHE.dynamic_top_cnf} bp. Région en tête de l'univers.`,
                `${LEADERS_D.find(r=>r.scope==='Market')?.name || '—'} : leader pays — ${LEADERS_D.find(r=>r.scope==='Market')?.leader_symbol || '—'}.`,
                `Top Picks : ${OPPS_D.slice(0,3).map(o=>`${o.sym} (IS ${o.is})`).join(', ')} — BUY, MOS >${Math.min(...OPPS_D.slice(0,3).map(o=>o.mos)).toFixed(0)}%.`,
              ]},
              {t:"Structure de marché", dot:C.gold, items:[
                `Secteurs en ACCUMULATION : ${SECTORS_D.filter(s=>s.flow_state==='ACCUMULATION').map(s=>s.name).join(', ')}.`,
                `Stratégie Top 1 : ${CACHE.top_strategy_name} +${CACHE.top_strategy_fy}% FY.`,
                `Univers : ${CACHE.buy_count} BUY / ${CACHE.watch_count.toLocaleString('fr-FR')} Watch / ${CACHE.avoid_count.toLocaleString('fr-FR')} Avoid — score ${CACHE.decision_score}/100.`,
              ]},
              {t:"Régime de marché", dot:C.red, items:[
                `Régime global : ${CACHE.market_regime}. ${CACHE.tooltip_regime}`,
                `Régions en distribution : ${RFLOWS.filter(r=>r.flow_state==='DISTRIBUTION').map(r=>r.region).join(', ')}.`,
                `Secteurs sous pression : ${SECTORS_D.filter(s=>s.flow_state==='ROTATION_BUY_SUPPORT'||s.flow_state==='DISTRIBUTION').map(s=>s.name).join(', ')}.`,
              ]},
            ].map(b => (
              <div key={b.t} style={{ borderRadius:12, border:"1px solid var(--line2)", padding:12, background:"var(--paper-alt)" }}>
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:8 }}>
                  <span style={{ fontSize:12, fontWeight:800, color:"var(--ink)" }}>{b.t}</span>
                  <div style={{ width:7, height:7, borderRadius:999, background:b.dot, flexShrink:0 }}/>
                </div>
                <ul style={{ listStyle:"none", display:"flex", flexDirection:"column", gap:5 }}>
                  {b.items.map((it,i) => <li key={i} style={{ fontSize:11, color:"var(--muted)", fontWeight:600, lineHeight:1.5 }}>• {it}</li>)}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Market Flow + Indices ── */}
      <div className="g-split" style={{ gap:12, alignItems:"stretch" }}>
        <MarketFlowCard th={th} nav={nav}/>
        <IndicesCard th={th}/>
      </div>

      {/* ── Leadership + Top Picks ── */}
      <div className="g-split2" style={{ gap:12 }}>
        <LeadershipCard openTicker={openTicker} />
        <OpportunitiesCard openTicker={openTicker} isMobile={false}/>
      </div>

      {/* ── Top Strategies + Strategy Top 1 côte à côte ── */}
      <div className="g-split2" style={{ gap:12, alignItems:"start" }}>
        <StrategiesChart th={th} fillPaired/>
        <StrategyCard th={th}/>
      </div>

    </main>
  );
}

/* ─── MARKET FLOW ─────────────────────────────────────────────── */
function ScatterSectors({ th, onNavigate, fillHeight }) {
  const { SECTORS_D_VIEW } = useHomeBundle();
  const stateColor = s =>
    s==="ACCUMULATION"        ? C.green  :
    s==="DISTRIBUTION"        ? C.red    :
    s==="ROTATION_BUY_SUPPORT"? "#4a9080":
    s==="NEUTRAL_VOLATILE"    ? "#8899aa":
    s==="ROTATION_SELL_PRESSURE" ? C.amber : "#8899aa";

  const hasXY = SECTORS_D_VIEW.some(d => d.cnf_pct != null && d.pnf != null);

  const stateX = {
    "ACCUMULATION":70, "NEUTRAL_VOLATILE":40,
    "ROTATION_BUY_SUPPORT":20, "ROTATION_SELL_PRESSURE":-10, "DISTRIBUTION":-30,
  };
  const data = hasXY
    ? SECTORS_D_VIEW.map(d => ({...d, x: d.pnf != null ? d.pnf : 0, y: d.cnf_pct != null ? d.cnf_pct : 0}))
    : SECTORS_D_VIEW.map(d => ({...d, x: stateX[d.flow_state] || 0, y: d.size * 1.2}));

  const xs = data.map(d=>d.x), ys = data.map(d=>d.y);
  const xMid = hasXY ? 0 : 50;
  const yMid = hasXY ? 0 : (Math.max(...ys)+Math.min(...ys))/2;
  const xD = [Math.min(...xs)-15, Math.max(...xs)+15];
  const yD = [Math.min(...ys)-8,  Math.max(...ys)+8];

  const Pt = ({cx=0,cy=0,payload}) => {
    const col = stateColor(payload.flow_state);
    const r = Math.max(5, Math.min(12, payload.size/4));
    return (
      <g style={{cursor:"pointer"}} onClick={()=>onNavigate&&onNavigate()}>
        <circle cx={cx} cy={cy} r={r} fill={col} opacity={.82} stroke="white" strokeWidth={1.5}/>
        <text x={cx} y={cy-r-3} fontSize={8} fontWeight={800} fill="var(--ink)" textAnchor="middle">
          {payload.name.split(" ")[0]}
        </text>
      </g>
    );
  };

  // Hauteur fixe maximisée — pas d'espace blanc en dessous
  const chartHeight = 280;

  return (
    <div style={{ borderRadius:12, border:"1px solid var(--line2)", padding:10, background:"var(--paper-alt)", cursor:"pointer" }} onClick={()=>onNavigate&&onNavigate()}>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:6 }}>
        <div>
          <div style={{ fontSize:12, fontWeight:800, color:"var(--ink)" }}>Sector Rotation — CNF</div>
          <div style={{ fontSize:9, color:"var(--muted)", fontWeight:600, marginTop:1 }}>
            {hasXY ? "PNF (axe X) × CNF% (axe Y) · taille = constituants" : "État CNF par secteur · taille = nb constituants"}
          </div>
        </div>
        <div style={{ display:"flex", gap:5, flexWrap:"wrap", justifyContent:"flex-end" }}>
          {[["ACCUM.",C.green],["ROT.",C.amber],["DISTRIB.",C.red]].map(([l,c])=>(
            <span key={l} style={{ fontSize:8, fontWeight:800, color:c, padding:"2px 5px", borderRadius:4, background:`${c}18`, border:`1px solid ${c}33` }}>{l}</span>
          ))}
        </div>
      </div>
      <div style={{ height: chartHeight }}>
        <ResponsiveContainer width="100%" height={chartHeight}>
          <ScatterChart margin={{top:10,right:10,bottom:4,left:-20}}>
            <CartesianGrid stroke="var(--line2)" strokeDasharray="3 3"/>
            <ReferenceLine x={xMid} stroke="var(--line)" strokeDasharray="4 3"/>
            <ReferenceLine y={yMid} stroke="var(--line)" strokeDasharray="4 3"/>
            <XAxis type="number" dataKey="x" domain={xD}
              tick={{fontSize:9,fill:"var(--muted)",fontWeight:700}} tickLine={false} axisLine={false}
              label={{value:"PNF →", position:"insideBottomRight", fontSize:8, fill:"var(--muted)", offset:-4}}/>
            <YAxis type="number" dataKey="y" domain={yD}
              tick={{fontSize:9,fill:"var(--muted)",fontWeight:700}} tickLine={false} axisLine={false}
              tickFormatter={v=>`${v}%`}/>
            <ZAxis dataKey="size" range={[30,200]}/>
            <Tooltip cursor={{strokeDasharray:"3 3",stroke:"var(--line)"}}
              contentStyle={{borderRadius:10,border:"1px solid var(--line)",background:th.paperSolid,color:"var(--ink)",fontSize:11}}
              formatter={(v,n,p) => {
                const d = p && p.payload;
                if (!d) return [v,n];
                return n==="x" ? [`PNF: ${v}%`,""] : [`CNF: ${v}%`,""];
              }}
              labelFormatter={(_,p)=> p && p[0] && p[0].payload ? p[0].payload.name : ""}/>
            <Scatter data={data} shape={<Pt/>}/>
          </ScatterChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function MarketFlowCard({ th, nav }) {
  const { CACHE, RFLOWS, regionTooltip } = useHomeBundle();
  const go = () => nav && nav("/markets");
  return (
    <div className="card" style={{ display:"flex", flexDirection:"column" }}>
      <div className="card-pad" style={{ display:"flex", flexDirection:"column", flex:1 }}>
        <SecHd icon="◉" title="Market Flow" sub="Flux de capitaux + rotation sectorielle" badge={<Bdg c="g">Risk selective</Bdg>} tooltip={CACHE.tooltip_market_flow}/>
        <div style={{ display:"flex", flexDirection:"column", gap:7 }}>
          {/* Header */}
          <div className="rf-row" style={{ padding:"4px 11px", fontSize:9, fontWeight:800, textTransform:"uppercase", letterSpacing:".08em", color:"var(--muted)" }}>
            <span>Région</span>
            <span style={{textAlign:"right"}}>CNF %</span>
            <span style={{textAlign:"center"}}>État</span>
          </div>
          {RFLOWS.map(r => (
            <div key={r.region} className="rf-row" style={{ borderRadius:11, border:"1px solid var(--line2)", padding:"9px 11px", background:"var(--paper-alt)", cursor:"pointer", transition:"background .12s", position:"relative" }}
              onClick={go}
              onMouseEnter={e=>e.currentTarget.style.background="var(--row-h)"}
              onMouseLeave={e=>e.currentTarget.style.background="var(--paper-alt)"}>
              <div style={{ display:"flex", alignItems:"center", gap:5 }}>
                <div style={{ fontSize:12, fontWeight:800, color:"var(--ink)" }}>{r.region}</div>
                <InvelpsTipMark title={regionTooltip(r.region)}/>
              </div>
              <div style={{ textAlign:"right", fontFamily:"'IBM Plex Mono',monospace", fontSize:12, fontWeight:800, color:r.cnf_pct>=0?C.green:C.red }}>
                {r.cnf_pct>0?"+":""}{r.cnf_pct.toFixed(1)}%
              </div>
              <div style={{ textAlign:"center", borderRadius:7, padding:"3px 6px", fontFamily:"'IBM Plex Mono',monospace", fontSize:10, fontWeight:800, color:"#fff",
                background:r.flow_state==="ACCUMULATION"?C.green:r.flow_state==="DISTRIBUTION"?C.red:C.amber }}>
                {r.flow_state==="ACCUMULATION"?"ACCUM.":r.flow_state==="DISTRIBUTION"?"DISTRIB.":r.flow_state==="ROTATION_BUY_SUPPORT"?"ROT.SUP":r.flow_state==="NEUTRAL_VOLATILE"?"NEUTRE":"ROTAT."}
              </div>
            </div>
          ))}
          <button onClick={go} style={{ marginTop:6, padding:"7px 12px", borderRadius:9, border:`1px solid ${C.gold}44`, background:"rgba(181,154,91,.08)", color:C.gold, fontSize:11, fontWeight:800, cursor:"pointer" }}>
            Voir tous les flux →
          </button>
        </div>
        {/* Scatter — prend tout l'espace restant, espace au-dessus */}
        <div style={{ flex:1, minHeight:220, marginTop:16 }}>
          <ScatterSectors th={th} onNavigate={go} fillHeight/>
        </div>
      </div>
    </div>
  );
}

/* ─── INDICES ─────────────────────────────────────────────────── */
function IndicesCard({ th }) {
  const { INDICES_D, CACHE } = useHomeBundle();
  const [reg, setReg] = useState("Tous");
  const REGS = ["Tous","Americas","Europe","Asia","Middle East"];
  const COL = "1fr 68px 54px 54px 54px";

  const rows = useMemo(() => {
    const base = reg === "Tous" ? [...INDICES_D] : INDICES_D.filter(x=>x.reg===reg);
    if (reg === "Tous") {
      // Sort by ret M-1 descending, nulls last
      return base.sort((a,b) => {
        if (a.ret==null && b.ret==null) return 0;
        if (a.ret==null) return 1;
        if (b.ret==null) return -1;
        return b.ret - a.ret;
      });
    }
    return base;
  }, [reg]);

  const IndexRow = ({ r, i }) => (
    <div style={{ display:"grid", gridTemplateColumns:COL, alignItems:"center", padding:"7px 12px", borderTop:i>0?"1px solid var(--line2)":"none" }}
      onMouseEnter={e=>e.currentTarget.style.background="var(--row-h)"}
      onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
      <div style={{ minWidth:0 }}>
        <div style={{ display:"flex", alignItems:"center", gap:6 }}>
          <div style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:11, fontWeight:800, color:"var(--ink)", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{r.name}</div>
          {reg === "Tous" && (
            <span style={{ fontSize:7, fontWeight:800, padding:"1px 4px", borderRadius:3, background:"rgba(181,154,91,.14)", color:C.gold, flexShrink:0 }}>{r.reg}</span>
          )}
        </div>
        <div style={{ fontSize:9, color:"var(--muted)", fontWeight:600 }}>{r.sym}</div>
      </div>
      <div style={{ textAlign:"right" }}>
        <span style={{ fontSize:8, fontWeight:800, padding:"2px 5px", borderRadius:4, background:`${indC(r.sig)}1e`, color:indC(r.sig), border:`1px solid ${indC(r.sig)}33` }}>
          {r.sig==="Bullish"?"▲ BULL":r.sig==="Bearish"?"▼ BEAR":"◈ NEU"}
        </span>
      </div>
      <div style={{ textAlign:"right", fontFamily:"'IBM Plex Mono',monospace", fontSize:11, fontWeight:800, color:r.vs50!=null?(r.vs50>=0?C.green:C.red):"var(--muted)" }}>
        {r.vs50!=null ? `${r.vs50>0?"+":""}${r.vs50.toFixed(1)}%` : "—"}
      </div>
      <div style={{ textAlign:"right", fontFamily:"'IBM Plex Mono',monospace", fontSize:11, fontWeight:800, color:r.vs200!=null?(r.vs200>=0?C.green:C.red):"var(--muted)" }}>
        {r.vs200!=null ? `${r.vs200>0?"+":""}${r.vs200.toFixed(1)}%` : "—"}
      </div>
      <div style={{ textAlign:"right", fontFamily:"'IBM Plex Mono',monospace", fontSize:11, fontWeight:800, color:r.ret!=null?(r.ret>=0?C.green:C.red):"var(--muted)" }}>
        {r.ret!=null ? `${r.ret>0?"+":""}${r.ret.toFixed(1)}%` : "—"}
      </div>
    </div>
  );

  return (
    <div className="card">
      <div className="card-pad">
        <SecHd icon="⊟" title="Grands Indices" sub="Signal · vs DMA50 · vs DMA200 · ret M-1" badge={<Bdg>{reg}</Bdg>} tooltip={CACHE.tooltip_indices}/>
        <div style={{ display:"flex", gap:6, flexWrap:"wrap", marginBottom:12 }}>
          {REGS.map(r => (
            <button key={r} onClick={()=>setReg(r)} style={{ borderRadius:999, border:`1px solid ${reg===r?C.gold:"var(--line)"}`, background:reg===r?"rgba(181,154,91,.14)":"transparent", color:reg===r?C.gold:"var(--muted)", padding:"4px 12px", fontSize:11, fontWeight:800 }}>{r}</button>
          ))}
        </div>
        <div style={{ borderRadius:12, overflow:"hidden", border:"1px solid var(--line)" }}>
          {/* Header */}
          <div style={{ display:"grid", gridTemplateColumns:COL, padding:"6px 12px", fontSize:9, fontWeight:800, textTransform:"uppercase", letterSpacing:".09em", background:"var(--paper-alt)", color:"var(--muted)", position:"sticky", top:0, zIndex:2 }}>
            <span>Indice</span>
            <span style={{textAlign:"right"}}>Signal</span>
            <span style={{textAlign:"right"}}>vs 50j</span>
            <span style={{textAlign:"right"}}>vs 200j</span>
            <span style={{textAlign:"right"}}>Ret M-1 {reg==="Tous" && "↓"}</span>
          </div>
          {rows.length === 0
            ? <div style={{ padding:"16px 12px", fontSize:11, color:"var(--muted)", textAlign:"center" }}>Aucun indice pour cette région</div>
            : rows.map((r,i) => <IndexRow key={r.sym} r={r} i={i}/>)
          }
        </div>
        {/* Mini synthèse bear/bull */}
        {reg === "Tous" && (() => {
          const bull = INDICES_D.filter(x=>x.sig==="Bullish").length;
          const bear = INDICES_D.filter(x=>x.sig==="Bearish").length;
          const neu  = INDICES_D.length - bull - bear;
          return (
            <div style={{ display:"flex", gap:8, marginTop:10 }}>
              {[["▲ Bullish", bull, C.green], ["◈ Neutre", neu, C.amber], ["▼ Bearish", bear, C.red]].map(([l,n,c])=>(
                <div key={l} style={{ flex:1, borderRadius:9, border:`1px solid ${c}33`, background:`${c}10`, padding:"6px 10px", textAlign:"center" }}>
                  <div style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:16, fontWeight:800, color:c, lineHeight:1 }}>{n}</div>
                  <div style={{ fontSize:8, fontWeight:800, color:c, marginTop:2, letterSpacing:".08em" }}>{l}</div>
                </div>
              ))}
            </div>
          );
        })()}
      </div>
    </div>
  );
}

/* ─── LEADERSHIP ──────────────────────────────────────────────── */
function LeadershipCard({ openTicker }) {
  const { CACHE, LEADERS_D } = useHomeBundle();

  const SCOPE_LABELS = {
    Region: "Région", Market: "Pays", Sector: "Secteur",
    Mkt_Class: "Capitalisation", Style: "Style"
  };

  const flowChip = (fs) => {
    const cfg = {
      "LEADER_CONFIRMED":    [C.green,  "LEADER ✓"],
      "LEADER_COOLING":      [C.amber,  "COOLING"],
      "ACCUMULATION":        [C.green,  "ACCUM."],
      "ROTATION_BUY_SUPPORT":[C.amber,  "ROTATION"],
      "NEUTRAL_VOLATILE":    ["var(--muted)", "NEUTRE"],
      "OUTFLOW":             [C.red,    "OUTFLOW"],
      "DISTRIBUTION":        [C.red,    "DISTRIB."],
    };
    const [c, label] = cfg[fs] || [C.amber, fs || "—"];
    return (
      <span style={{ fontSize:8, fontWeight:800, padding:"1px 6px", borderRadius:4,
        background:`${c}18`, color:c, border:`1px solid ${c}44`,
        fontFamily:"'IBM Plex Mono',monospace", letterSpacing:".06em", whiteSpace:"nowrap" }}>
        {label}
      </span>
    );
  };

  const rankColor = (i) => i < 3 ? "#3d7068" : C.gold;

  return (
    <div className="card">
      <div className="card-pad">
        <SecHd icon="◈" title="Leadership Board"
          sub="Séquence top-down : Région → Pays → Secteur → Cap → Style"
          badge={<Bdg>Top 5</Bdg>}
          tooltip={CACHE.tooltip_leaders}/>

        <div style={{ display:"flex", flexDirection:"column", gap:6, position:"relative" }}>
          {/* Ligne de connexion verticale */}
          <div style={{ position:"absolute", left:19, top:28, bottom:28, width:2,
            background:"linear-gradient(180deg,var(--line2) 0%,var(--line2) 100%)",
            borderRadius:1, zIndex:0 }}/>

          {LEADERS_D.map((r, i) => (
            <div key={r.rank}
              style={{ display:"flex", gap:10, alignItems:"flex-start",
                borderRadius:12, border:"1px solid var(--line2)", padding:"10px 12px",
                background:"var(--paper-alt)", transition:"background .12s",
                position:"relative", zIndex:1, cursor:"pointer" }}
              onMouseEnter={e=>e.currentTarget.style.background="var(--row-h)"}
              onMouseLeave={e=>e.currentTarget.style.background="var(--paper-alt)"}>

              {/* Rang */}
              <div style={{ width:30, height:30, borderRadius:999, flexShrink:0,
                display:"flex", alignItems:"center", justifyContent:"center",
                fontFamily:"'IBM Plex Mono',monospace", fontSize:11, fontWeight:800,
                color:"#fff", background:rankColor(i) }}>
                {r.rank}
              </div>

              {/* Contenu central */}
              <div style={{ flex:1, minWidth:0 }}>
                {/* Ligne 1 : scope + nom du groupe + flow_state */}
                <div style={{ display:"flex", alignItems:"center", gap:5, flexWrap:"wrap", marginBottom:3 }}>
                  <span style={{ fontSize:8, fontWeight:800, padding:"1px 5px", borderRadius:3,
                    background:"rgba(181,154,91,.14)", color:C.gold,
                    textTransform:"uppercase", letterSpacing:".08em" }}>
                    {SCOPE_LABELS[r.scope] || r.scope}
                  </span>
                  <span style={{ fontSize:13, fontWeight:800, color:"var(--ink)", lineHeight:1 }}>
                    {r.name}
                  </span>
                  {flowChip(r.flow_state)}
                </div>

                {/* Ligne 2 : symbole leader + secteur + pays */}
                <div style={{ display:"flex", alignItems:"center", gap:8, flexWrap:"wrap", marginTop:2 }}>
                  {r.leader_symbol && (
                    <TickerSymbolLink
                      symbol={r.leader_symbol}
                      onOpen={openTicker}
                      truncate={0}
                      style={{
                        fontSize: 10,
                        background: "var(--line2)",
                        borderRadius: 5,
                        padding: "1px 6px",
                        textDecoration: "none",
                      }}
                    />
                  )}
                  {r.sector && (
                    <span style={{ fontSize:9, color:"var(--muted)", fontWeight:600 }}>
                      {r.sector}
                    </span>
                  )}
                  {r.market_iso2 && (
                    <span style={{ fontSize:9, color:"var(--muted)", fontWeight:600 }}>
                      · {r.market_iso2}
                    </span>
                  )}
                </div>

                {/* Ligne 3 : mkt_class + style */}
                <div style={{ fontSize:9, color:"var(--muted)", fontWeight:600, marginTop:2 }}>
                  {[r.mkt_class, r.style].filter(Boolean).join(" · ")}
                </div>
              </div>

              {/* Droite : ret_m1 */}
              <div style={{ flexShrink:0, textAlign:"right" }}>
                {r.ret_m1 != null && (
                  <div style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:12,
                    fontWeight:800, color:r.ret_m1 >= 0 ? C.green : C.red }}>
                    {r.ret_m1 > 0 ? "+" : ""}{r.ret_m1.toFixed(1)}%
                  </div>
                )}
                {r.persistence > 0 && (
                  <div style={{ fontSize:8, color:"var(--muted)", fontWeight:600, marginTop:2 }}>
                    {r.persistence}m persist.
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Lecture synthétique top-down */}
        <div style={{ marginTop:10, borderRadius:11, border:`1px solid ${C.gold}30`,
          padding:"10px 14px", background:`${C.gold}08`,
          display:"flex", alignItems:"flex-start", gap:10 }}>
          <div style={{ fontSize:14, flexShrink:0, marginTop:1 }}>◈</div>
          <div>
            <div style={{ fontSize:9, fontWeight:800, textTransform:"uppercase",
              letterSpacing:".11em", color:C.gold, marginBottom:4 }}>
              Lecture top-down
            </div>
            <div style={{ fontSize:11, color:"var(--muted)", fontWeight:600, lineHeight:1.55 }}>
              {LEADERS_D.map(r => r.name).join(" → ")}.
              Leader titre : {LEADERS_D.find(r=>r.scope==="Sector")?.leader_symbol || "—"} ({LEADERS_D.find(r=>r.scope==="Sector")?.sector || "—"}).
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─── STRATEGY ────────────────────────────────────────────────── */
function StrategyCard({ th }) {
  const { STRATS_D, STRAT_CURVE, CACHE } = useHomeBundle();
  const top = useMemo(()=>[...STRATS_D].sort((a,b)=>b.fy-a.fy)[0],[STRATS_D]);
  const donut = [{name:"α",value:10.1,fill:C.green},{name:"β",value:2.3,fill:C.gold}];
  return (
    <div className="card">
      <div className="card-pad">
        <SecHd icon="▣" title="Strategy Top 1" sub="Stratégie Invelps la plus performante" badge={<Bdg c="g">+128%</Bdg>}/>
        <div className="g2" style={{ gap:12, alignItems:"center" }}>
          <div style={{ position:"relative", height:160 }}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={donut} dataKey="value" innerRadius={48} outerRadius={68} startAngle={90} endAngle={-270} paddingAngle={2}>{donut.map(d=><Cell key={d.name} fill={d.fill}/>)}</Pie>
                <Tooltip contentStyle={{borderRadius:10,border:"1px solid var(--line)",background:th.paperSolid,color:"var(--ink)",fontSize:11}} formatter={v=>fP(v)}/>
              </PieChart>
            </ResponsiveContainer>
            <div style={{ position:"absolute", inset:0, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", pointerEvents:"none" }}>
              <div style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:22, fontWeight:800, color:C.green }}>+12.4%</div>
              <div style={{ fontSize:9, color:"var(--muted)", fontWeight:700, textAlign:"center", marginTop:1 }}>T3 2024</div>
            </div>
          </div>
          <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
            <div style={{ borderRadius:11, border:"1px solid var(--line2)", padding:10, background:"var(--paper-alt)" }}>
              <div style={{ fontSize:9, fontWeight:800, textTransform:"uppercase", letterSpacing:".11em", color:"var(--muted)" }}>Stratégie</div>
              <div style={{ fontSize:12, fontWeight:800, color:"var(--ink)", marginTop:3 }}>{top.s}</div>
              <div style={{ fontSize:11, color:"var(--muted)", marginTop:1 }}>{top.a}</div>
            </div>
            {[["α généré", `+${CACHE.alpha_top_strategy}%`, C.green],["β CAGR 5Y SPX","+12.73%","var(--muted)"],["Benchmark SPX 2024","+23.31%","var(--muted)"]].map(([l,v,c])=>(
              <div key={l} style={{ display:"flex", justifyContent:"space-between", fontSize:12, fontWeight:800 }}>
                <span style={{color:"var(--ink)"}}>{l}</span><span style={{fontFamily:"'IBM Plex Mono',monospace",color:c}}>{v}</span>
              </div>
            ))}
          </div>
        </div>
        <div style={{ marginTop:12, borderRadius:11, border:"1px solid var(--line2)", padding:10, background:"var(--paper-alt)" }}>
          <div style={{ fontSize:11, fontWeight:800, color:"var(--ink)", marginBottom:8 }}>Performance cumulée</div>
          <div style={{ height:110 }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={STRAT_CURVE} margin={{top:4,right:6,bottom:0,left:-22}}>
                <CartesianGrid stroke="var(--line2)" vertical={false}/>
                <XAxis dataKey="m" tick={{fontSize:9,fill:"var(--muted)",fontWeight:700}} tickLine={false} axisLine={false}/>
                <YAxis tick={{fontSize:9,fill:"var(--muted)",fontWeight:700}} tickLine={false} axisLine={false} tickFormatter={v=>`${v}%`}/>
                <Tooltip contentStyle={{borderRadius:10,border:"1px solid var(--line)",background:th.paperSolid,color:"var(--ink)",fontSize:11}} formatter={v=>fP(v)}/>
                <Line type="monotone" dataKey="s" connectNulls={false} name="Stratégie" stroke={C.green} strokeWidth={2.5} dot={false}/>
                <Line type="monotone" dataKey="b" name="Benchmark" stroke="var(--muted)" strokeWidth={2} dot={false} strokeDasharray="4 3"/>
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="g3" style={{ gap:8, marginTop:12 }}>
          <Kpi label="Sharpe" value="1.42"  sub="ratio"    tone="g"/>
          <Kpi label="Max DD" value="-6.3%" sub="drawdown" tone="r"/>
          <Kpi label="Capture" value="112%" sub="hausse"   tone="g"/>
        </div>
      </div>
    </div>
  );
}

/* ─── STRATEGIES CHART ────────────────────────────────────────── */
function StrategiesChart({ th, fillPaired }) {
  const { STRATS_D, CACHE } = useHomeBundle();
  const [n, setN] = useState(fillPaired ? STRATS_D.length : 8);
  useEffect(() => {
    if (fillPaired) setN(STRATS_D.length);
  }, [fillPaired, STRATS_D]);
  const rows = useMemo(()=>[...STRATS_D].sort((a,b)=>b.fy-a.fy).slice(0,n),[n]);
  // Hauteur : ~26px par barre + marges
  const chartH = Math.max(160, rows.length * 28 + 20);
  return (
    <div className="card">
      <div className="card-pad">
        <SecHd icon="▣" title="Top Stratégies" sub="FY Monthly classement" badge={<Bdg>Top {n}</Bdg>} tooltip={CACHE.tooltip_strategies}/>
        <div style={{ display:"flex", justifyContent:"flex-end", gap:6, marginBottom:10 }}>
          {(fillPaired ? [5,8,STRATS_D.length] : [5,8]).map(x=><button key={x} onClick={()=>setN(x)} style={{ borderRadius:999, border:`1px solid ${n===x?C.gold:"var(--line)"}`, background:n===x?"rgba(181,154,91,.14)":"transparent", color:n===x?C.gold:"var(--muted)", padding:"3px 11px", fontSize:10, fontWeight:800 }}>{x===STRATS_D.length?"Tous":`Top ${x}`}</button>)}
        </div>
        <div style={{ borderRadius:12, border:"1px solid var(--line2)", padding:10, background:"var(--paper-alt)", height:chartH }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={rows} layout="vertical" margin={{top:0,right:46,bottom:0,left:0}}>
              <CartesianGrid stroke="var(--line2)" horizontal={false}/>
              <XAxis type="number" hide/>
              <YAxis type="category" dataKey="s" connectNulls={false} width={164} tickLine={false} axisLine={false} tick={{fontSize:9,fill:"var(--muted)",fontWeight:700}}/>
              <Tooltip cursor={{fill:"var(--row-h)"}} contentStyle={{borderRadius:10,border:"1px solid var(--line)",background:th.paperSolid,color:"var(--ink)",fontSize:11}} formatter={v=>[fP(v),"FY"]}/>
              <Bar dataKey="fy" radius={[0,9,9,0]} barSize={14}>{rows.map((e,i)=><Cell key={i} fill={e.fy>=55?C.green:e.fy>=35?C.amber:C.orange}/>)}<LabelList dataKey="fy" position="right" formatter={v=>`${(+v).toFixed(1)}%`} style={{fill:"var(--ink)",fontSize:9,fontWeight:900}}/></Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}

/* ─── TOP PICKS ───────────────────────────────────────────────── */
function OpportunitiesCard({ openTicker }) {
  const { OPPS_D, CACHE } = useHomeBundle();
  const [n, setN] = useState(8);
  const rows = OPPS_D.slice(0, n);
  // Desktop: chip BUY + Thèse | Mobile: sans Thèse
  const COL_MOBILE = "minmax(0,1fr) 50px 46px 46px 46px 48px";
  const COL_DESKTOP = "minmax(0,1.2fr) 50px 46px 46px 46px 48px minmax(0,1.8fr)";

  const [wide, setWide] = useState(() => window.innerWidth >= 900);
  useEffect(() => {
    const fn = () => setWide(window.innerWidth >= 900);
    window.addEventListener("resize", fn);
    return () => window.removeEventListener("resize", fn);
  }, []);

  const COL = wide ? COL_DESKTOP : COL_MOBILE;

  return (
    <div className="card">
      <div className="card-pad">
        <SecHd icon="✦" title="Top Picks" sub="Meilleures opportunités · signal BUY · IScore élevé" badge={<Bdg>Top {n}</Bdg>} tooltip={CACHE.tooltip_top_picks}/>
        <div style={{ display:"flex", justifyContent:"flex-end", gap:6, marginBottom:10 }}>
          {[5,8].map(x=><button key={x} onClick={()=>setN(x)} style={{ borderRadius:999, border:`1px solid ${n===x?C.gold:"var(--line)"}`, background:n===x?"rgba(181,154,91,.14)":"transparent", color:n===x?C.gold:"var(--muted)", padding:"3px 11px", fontSize:10, fontWeight:800 }}>Top {x}</button>)}
        </div>
        {/* Header */}
        <div style={{ display:"grid", gridTemplateColumns:COL, gap:6, padding:"5px 10px 7px", fontSize:9, fontWeight:800, textTransform:"uppercase", letterSpacing:".08em", color:"var(--muted)", borderBottom:`2px solid var(--line)` }}>
          <span style={{textAlign:"left"}}>Valeur</span>
          <span style={{textAlign:"center"}}>Signal</span>
          <span style={{textAlign:"right"}}>IS</span>
          <span style={{textAlign:"right"}}>Fund</span>
          <span style={{textAlign:"right"}}>Trend</span>
          <span style={{textAlign:"right"}}>MOS</span>
          {wide && <span style={{textAlign:"left"}}>Thèse d'investissement</span>}
        </div>
        {rows.map((o,i) => {
          const mosColor = o.mos>=20?C.green:o.mos>=0?C.amber:C.red;
          return (
            <button key={o.sym} onClick={()=>openTicker && openTicker(o.sym)}
              style={{ display:"grid", gridTemplateColumns:COL, gap:6, width:"100%", background:"none", border:"none", borderTop:i?"1px solid var(--line2)":"none", cursor:"pointer", padding:"10px 10px", alignItems:"center" }}
              onMouseEnter={e=>e.currentTarget.style.background="var(--row-h)"}
              onMouseLeave={e=>e.currentTarget.style.background="none"}>
              {/* Titre */}
              <div style={{ minWidth:0, overflow:"hidden" }}>
                <div style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:11, fontWeight:800, color:"var(--ink)", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{o.sym}</div>
                <div style={{ fontSize:9, color:"var(--muted)", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", marginTop:1 }}>{o.name}</div>
              </div>
              {/* BUY chip */}
              <div style={{ textAlign:"center" }}>
                <span style={{ fontSize:8, fontWeight:800, padding:"2px 6px", borderRadius:4, background:`${C.green}18`, color:C.green, border:`1px solid ${C.green}44`, fontFamily:"'IBM Plex Mono',monospace" }}>
                  {o.sig}
                </span>
              </div>
              {/* IScore */}
              <div style={{ textAlign:"right" }}>
                <div style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:11, fontWeight:800, color:sC(o.is), lineHeight:1 }}>{o.is.toFixed(1)}</div>
                <div style={{ height:3, borderRadius:999, background:"var(--line2)", marginTop:3, overflow:"hidden" }}>
                  <div style={{ height:"100%", width:`${o.is}%`, background:sC(o.is), borderRadius:999 }}/>
                </div>
              </div>
              {/* Fund */}
              <div style={{ textAlign:"right" }}>
                <div style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:11, fontWeight:800, color:sC(o.fs), lineHeight:1 }}>{o.fs.toFixed(1)}</div>
                <div style={{ height:3, borderRadius:999, background:"var(--line2)", marginTop:3, overflow:"hidden" }}>
                  <div style={{ height:"100%", width:`${o.fs}%`, background:sC(o.fs), borderRadius:999 }}/>
                </div>
              </div>
              {/* Trend */}
              <div style={{ textAlign:"right" }}>
                <div style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:11, fontWeight:800, color:sC(o.tr), lineHeight:1 }}>{o.tr.toFixed(1)}</div>
                <div style={{ height:3, borderRadius:999, background:"var(--line2)", marginTop:3, overflow:"hidden" }}>
                  <div style={{ height:"100%", width:`${o.tr}%`, background:sC(o.tr), borderRadius:999 }}/>
                </div>
              </div>
              {/* MOS */}
              <div style={{ textAlign:"right", fontFamily:"'IBM Plex Mono',monospace", fontSize:11, fontWeight:800, color:mosColor }}>
                {Math.min(999,o.mos).toFixed(0)}%
              </div>
              {/* Thèse — desktop only */}
              {wide && (
                <div style={{ minWidth:0, paddingLeft:6, borderLeft:"1px solid var(--line2)" }}>
                  <div style={{ fontSize:10, color:"var(--muted)", fontWeight:600, lineHeight:1.45, overflow:"hidden", display:"-webkit-box", WebkitLineClamp:2, WebkitBoxOrient:"vertical" }}>
                    {o.these}
                  </div>
                </div>
              )}
            </button>
          );
        })}
        {/* Moat summary */}
        <div style={{ display:"flex", gap:6, marginTop:10, flexWrap:"wrap" }}>
          {[["Wide", C.green], ["Narrow", C.amber], ["None", C.red]].map(([m,c])=>{
            const cnt = rows.filter(o=>o.moat===m).length;
            if (!cnt) return null;
            return <span key={m} style={{ fontSize:9, padding:"2px 8px", borderRadius:4, background:`${c}14`, color:c, border:`1px solid ${c}33`, fontWeight:800 }}>{cnt}× {m}</span>;
          })}
        </div>
      </div>
    </div>
  );
}

/* ─── MARKETS PAGE ────────────────────────────────────────────── */
function MarketsPage({ th }) {
  return (
    <main className="page">
      <div style={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between", flexWrap:"wrap", gap:10 }}>
        <div>
          <h1 style={{ fontFamily:"'Cormorant Garamond',Georgia,serif", fontSize:"clamp(22px,4vw,34px)", fontWeight:700, color:"var(--ink)", letterSpacing:"-.03em" }}>Marchés</h1>
          <p style={{ fontSize:12, color:"var(--muted)", fontWeight:600, marginTop:2 }}>Flux · Leaders · Rotation sectorielle · Indices</p>
        </div>
        <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}><Bdg c="w">Risk-off sélectif</Bdg><Bdg>Nov. 2025</Bdg></div>
      </div>
      <div className="g-split" style={{ gap:12, alignItems:"stretch" }}><MarketFlowCard th={th}/><IndicesCard th={th}/></div>
      <div className="g-split" style={{ gap:12 }}><LeadershipCard/><StrategyCard th={th}/></div>
      <StrategiesChart th={th}/>
    </main>
  );
}

/* ─── PRX DATA ────────────────────────────────────────────────── */
const PRX = {
  sym:"PRX.AS",name:"Prosus N.V.",price:40.02,cur:"EUR",mtd:-10.41,ytd:-26.24,
  exch:"Euronext Amsterdam",sector:"Communication Services",cap:"Large Cap",moat:"Narrow",beta:0.769,
  desc:"Prosus N.V. est un groupe d'internet et d'e-commerce mondial opérant dans les classifiés, paiements, fintech, livraison de repas, edtech, e-tail, santé et médias sociaux en Amérique Latine, Afrique, Europe et Asie.",
  fs:72.16,fl:"Satisfaisant",
  pillars:[
    {n:"Liquidité",s:100,t:"→",l:"Excellent"},{n:"Profitabilité",s:77.4,t:"→",l:"Solide"},
    {n:"Croissance",s:92,t:"→",l:"Excellent"},{n:"Efficacité",s:93.5,t:"→",l:"Excellent"},
    {n:"Rentabilité",s:76.6,t:"→",l:"Solide"},{n:"Cash Flow",s:78.1,t:"↑",l:"Solide"},
    {n:"Solvabilité",s:80.2,t:"→",l:"Solide"},{n:"Alloc. Capital",s:56,t:"→",l:"Fragile"},
    {n:"Création val.",s:19.1,t:"↓",l:"Insuffisante"},
  ],
  ts:39.7,tPrice:43.51,sma20:44.45,sma50:49.31,sma200:52.18,rsi:31.16,
  rrQ:"Déséquilibré",rrCAGR:2.97,rrDD:-32.09,rrSh:-0.001,
  dcf:29.56,mos:-35.39,pe:7.58,pb:1.86,eveb:12.33,fcfy:0.0085,
  efCov:12.65,efL:"Levier externe",
  efItems:[
    {n:"BFR",m:106,pct:100,ok:true,mult:9.86},{n:"CAPEX",m:67,pct:100,ok:true,mult:15.60},
    {n:"Service dette",m:614,pct:100,ok:true,mult:1.70},{n:"Actionnaires",m:7476,pct:13.98,ok:false,mult:0.14},
  ],
};

/* ─── STOCK DASHBOARD ─────────────────────────────────────────── */
function DashSec({ title, sub, accent, children }) {
  return (
    <div style={{ background:"var(--paper)", borderRadius:14, padding:14, border:"1px solid var(--line)", borderTop:accent?`3px solid ${C.gold}`:"1px solid var(--line)", boxShadow:"0 2px 10px rgba(16,40,28,.06)" }}>
      <div style={{ fontWeight:900, fontSize:14, color:"var(--ink)", marginBottom:2 }}>{title}</div>
      {sub && <div style={{ fontSize:11, color:"var(--muted)", marginBottom:12, fontWeight:600 }}>{sub}</div>}
      {children}
    </div>
  );
}

/* eslint-disable-next-line no-unused-vars -- démo locale; route /stock redirigée vers TickerAnalysis */
function StockDashboard({ sym, nav, th }) {
  const [tab, setTab] = useState("overview");
  const d = PRX;
  const TABS = [{id:"overview",l:"Vue générale"},{id:"fund",l:"Fondamentaux"},{id:"timing",l:"Timing"},{id:"val",l:"Valorisation"},{id:"rr",l:"R/R"},{id:"ef",l:"État Fin."}];

  return (
    <div style={{ maxWidth:680, margin:"0 auto", paddingBottom:48 }}>
      <div style={{ background:`linear-gradient(160deg,${th.nav0},${th.nav1})`, padding:"14px 14px 0" }}>
        <button onClick={()=>nav("/")} style={{ background:"none", border:"none", color:"rgba(255,255,255,.55)", fontSize:12, fontWeight:700, marginBottom:10, display:"flex", alignItems:"center", gap:4, padding:0, cursor:"pointer" }}>← Retour</button>
        <div className="stock-hdr">
          <div>
            <div style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:20, fontWeight:800, color:"#fff", lineHeight:1 }}>{sym}</div>
            <div style={{ fontSize:12, color:"rgba(201,169,110,.9)", marginTop:3 }}>{d.name}</div>
            <div style={{ fontSize:10, color:"rgba(255,255,255,.45)", marginTop:1 }}>{d.exch} · {d.sector}</div>
          </div>
          <div>
            <div style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:20, fontWeight:800, color:"#fff" }}>{d.price.toFixed(2)} {d.cur}</div>
            <div style={{ fontSize:11, fontWeight:700, color:d.ytd>=0?"rgba(201,169,110,.9)":"#f08070", marginTop:2 }}>YTD {d.ytd>=0?"+":""}{d.ytd.toFixed(2)}% · MTD {d.mtd.toFixed(2)}%</div>
            <div style={{ display:"flex", gap:6, flexWrap:"wrap", marginTop:8 }}>
              <SBdg tone={d.fs>=75?"g":d.fs>=55?"w":"r"} label={`Fund ${d.fs.toFixed(0)}`}/>
              <SBdg tone="o" label={d.rrQ}/>
              <SBdg tone="r" label={`TScore ${d.ts}`}/>
            </div>
          </div>
        </div>
        <div className="tabs" style={{ marginTop:12 }}>
          {TABS.map(t=>(
            <button key={t.id} onClick={()=>setTab(t.id)} style={{ flexShrink:0, padding:"10px 13px", border:"none", background:"transparent", cursor:"pointer", fontSize:12, fontWeight:700, color:tab===t.id?"#fff":"rgba(255,255,255,.46)", borderBottom:tab===t.id?`2.5px solid ${C.gold}`:"2.5px solid transparent", whiteSpace:"nowrap", transition:"all .15s" }}>{t.l}</button>
          ))}
        </div>
      </div>

      <div style={{ padding:"12px 14px", display:"flex", flexDirection:"column", gap:10 }}>
        {tab==="overview" && <>
          <DashSec title="Profil" sub={`${d.cap} · ${d.moat} Moat · β ${d.beta}`} accent>
            <p style={{ fontSize:12, lineHeight:1.75, color:"var(--muted)", marginBottom:12 }}>{d.desc}</p>
            <div className="g4" style={{ gap:8 }}>
              {[["Capitalisation","115.2B EUR"],["Employés","21 048"],["IPO","2019-09-11"],["Pays","NL"]].map(([l,v])=>(
                <div key={l} style={{ borderRadius:10, padding:"8px 10px", background:"var(--paper-alt)", border:"1px solid var(--line)" }}>
                  <div style={{ fontSize:9, color:"var(--muted)", fontWeight:600, marginBottom:2 }}>{l}</div>
                  <div style={{ fontSize:12, fontWeight:800, color:"var(--ink)" }}>{v}</div>
                </div>
              ))}
            </div>
          </DashSec>
          <DashSec title="Scores synthétiques">
            <div className="g4" style={{ gap:8 }}>
              {[{l:"Fondamentaux",v:d.fs,n:d.fl},{l:"TScore",v:d.ts,n:"Signal faible"},{l:"Valorisation",v:39,n:"Surcoté"},{l:"R/R",v:25,n:d.rrQ}].map(s=>{
                const c=sC(s.v);
                return <div key={s.l} style={{ borderRadius:12, padding:12, background:"var(--paper-alt)", border:"1px solid var(--line)" }}>
                  <div style={{ fontSize:10, color:"var(--muted)", fontWeight:600, marginBottom:4 }}>{s.l}</div>
                  <div style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:20, fontWeight:800, color:c, lineHeight:1 }}>{s.v.toFixed(0)}</div>
                  <div style={{ fontSize:9, color:c, fontWeight:700, marginTop:2 }}>{s.n}</div>
                  <div style={{ marginTop:6 }}><SBar value={s.v}/></div>
                </div>;
              })}
            </div>
          </DashSec>
          <DashSec title="Signaux clés">
            {[{i:"◉",l:"Structure",v:"Compétition équilibrée",t:"g"},{i:"◷",l:"Timing",v:`TScore ${d.ts} — Signal faible`,t:"r"},{i:"◎",l:"DCF",v:`MOS ${d.mos.toFixed(1)}% — Surcoté`,t:"r"},{i:"◈",l:"R/R",v:`${d.rrQ} — CAGR ${d.rrCAGR.toFixed(1)}%`,t:"o"},{i:"≡",l:"CFO Coverage",v:`${d.efCov.toFixed(1)}% — ${d.efL}`,t:"r"}].map((s,idx)=>(
              <div key={idx} style={{ display:"flex", alignItems:"center", gap:10, padding:"9px 10px", background:"var(--paper-alt)", borderRadius:11, border:"1px solid var(--line)", marginBottom:7 }}>
                <span style={{ fontSize:14, color:C.gold, flexShrink:0, width:18 }}>{s.i}</span>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontSize:10, color:"var(--muted)", fontWeight:600 }}>{s.l}</div>
                  <div style={{ fontSize:11, fontWeight:800, color:"var(--ink)", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{s.v}</div>
                </div>
                <SBdg tone={s.t} label={s.t==="g"?"✓":s.t==="r"?"✗":"⚠"}/>
              </div>
            ))}
          </DashSec>
        </>}

        {tab==="fund" && <DashSec title="Fondamentaux" sub={`${d.fs.toFixed(1)}/100 — ${d.fl}`} accent>
          <div style={{ marginBottom:12 }}><SBar value={d.fs}/></div>
          {d.pillars.map((p,i)=>(
            <div key={i} className="pillar-row" style={{ padding:"7px 0", borderBottom:"1px solid var(--line2)" }}>
              <span style={{ fontSize:12, fontWeight:700, color:"var(--ink)" }}>{p.n}</span>
              <span style={{ fontSize:10, fontWeight:700, color:sC(p.s), textAlign:"right" }}>{p.l}</span>
              <SBar value={p.s}/>
              <span style={{ fontSize:13, color:p.t==="↑"?C.green:p.t==="↓"?C.red:"var(--muted)", fontWeight:800, textAlign:"right" }}>{p.t}</span>
            </div>
          ))}
        </DashSec>}

        {tab==="timing" && <DashSec title="Timing" sub={`TScore ${d.ts} — Signal faible · éviter`} accent>
          <div style={{ marginBottom:12 }}><SBar value={d.ts}/></div>
          <div className="g4" style={{ gap:8, marginBottom:12 }}>
            {[["Prix",d.tPrice],["SMA 20",d.sma20],["SMA 50",d.sma50],["SMA 200",d.sma200],["RSI 14",d.rsi.toFixed(1)],["Wyckoff","B"]].map(([l,v])=>(
              <div key={l} style={{ borderRadius:10, padding:"8px 10px", background:"var(--paper-alt)", border:"1px solid var(--line)" }}>
                <div style={{ fontSize:9, color:"var(--muted)", fontWeight:600 }}>{l}</div>
                <div style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:12, fontWeight:800, color:"var(--ink)", marginTop:2 }}>{v}</div>
              </div>
            ))}
          </div>
          <div style={{ padding:12, background:"rgba(192,74,56,.09)", borderRadius:11, border:"1px solid rgba(192,74,56,.22)" }}>
            <div style={{ fontSize:11, fontWeight:800, color:C.red, marginBottom:3 }}>⚠ Prix sous la SMA200 ({d.sma200} EUR)</div>
            <div style={{ fontSize:11, color:"var(--ink)", lineHeight:1.6 }}>Structure technique défavorable. RSI {d.rsi.toFixed(1)} — zone de faiblesse.</div>
          </div>
        </DashSec>}

        {tab==="val" && <DashSec title="Valorisation" sub="Strongly Overvalued — DCF" accent>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:12, background:"rgba(192,74,56,.08)", borderRadius:12, border:"1px solid rgba(192,74,56,.22)", marginBottom:12, flexWrap:"wrap", gap:10 }}>
            <div><div style={{ fontSize:10, color:"var(--muted)", fontWeight:600 }}>Valeur DCF</div><div style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:20, fontWeight:800, color:C.red }}>{d.dcf.toFixed(2)} {d.cur}</div></div>
            <div style={{ textAlign:"right" }}><div style={{ fontSize:10, color:"var(--muted)", fontWeight:600 }}>Prix marché</div><div style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:20, fontWeight:800, color:"var(--ink)" }}>{d.price.toFixed(2)} {d.cur}</div></div>
          </div>
          <div className="g4" style={{ gap:8 }}>
            {[["MOS",`${d.mos.toFixed(1)}%`,"r"],["P/E",d.pe.toFixed(2),""],["P/B",d.pb.toFixed(2),""],["EV/EBITDA",d.eveb.toFixed(2),""],["FCF Yield",`${(d.fcfy*100).toFixed(2)}%`,"w"],["Upside","-26.1%","r"]].map(([l,v,t])=>(
              <div key={l} style={{ borderRadius:10, padding:"8px 10px", background:"var(--paper-alt)", border:"1px solid var(--line)" }}>
                <div style={{ fontSize:9, color:"var(--muted)", fontWeight:600 }}>{l}</div>
                <div style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:12, fontWeight:800, color:t==="r"?C.red:t==="w"?C.amber:"var(--ink)", marginTop:2 }}>{v}</div>
              </div>
            ))}
          </div>
        </DashSec>}

        {tab==="rr" && <DashSec title="Risque / Rendement" sub={`${d.rrQ} — Profil peu favorable`} accent>
          <div style={{ padding:12, background:"rgba(210,138,82,.09)", borderRadius:12, border:"1px solid rgba(210,138,82,.28)", marginBottom:12 }}>
            <div style={{ fontWeight:900, color:C.orange, fontSize:13, marginBottom:5 }}>{d.rrQ}</div>
            <p style={{ fontSize:12, color:"var(--ink)", lineHeight:1.6 }}>CAGR {d.rrCAGR.toFixed(2)}% vs drawdown {d.rrDD.toFixed(2)}%. Sharpe {d.rrSh.toFixed(4)}.</p>
          </div>
          <div className="g4" style={{ gap:8 }}>
            {[["CAGR 5Y",`${d.rrCAGR.toFixed(2)}%`,"w"],["DD Max",`${d.rrDD.toFixed(2)}%`,"r"],["Volatilité","23.5%","w"],["Sharpe",d.rrSh.toFixed(4),"r"]].map(([l,v,t])=>(
              <div key={l} style={{ borderRadius:10, padding:"8px 10px", background:"var(--paper-alt)", border:"1px solid var(--line)" }}>
                <div style={{ fontSize:9, color:"var(--muted)", fontWeight:600 }}>{l}</div>
                <div style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:12, fontWeight:800, color:t==="r"?C.red:t==="w"?C.amber:C.green, marginTop:2 }}>{v}</div>
              </div>
            ))}
          </div>
        </DashSec>}

        {tab==="ef" && <DashSec title="État Financier" sub={`CFO Coverage ${d.efCov.toFixed(1)}% — ${d.efL}`} accent>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", flexWrap:"wrap", gap:6, marginBottom:10 }}>
            <span style={{ fontSize:12, fontWeight:800, color:"var(--ink)" }}>CFO 1 045 M€ vs Besoins</span>
            <SBdg tone="r" label="Levier externe"/>
          </div>
          <div style={{ marginBottom:14 }}><SBar value={d.efCov}/></div>
          {d.efItems.map((item,i)=>(
            <div key={i} style={{ marginBottom:8, padding:11, background:item.ok?"rgba(111,138,91,.08)":"rgba(192,74,56,.08)", borderRadius:11, border:`1px solid ${item.ok?C.green+"33":C.red+"33"}` }}>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", flexWrap:"wrap", gap:6, marginBottom:6 }}>
                <span style={{ fontSize:12, fontWeight:800, color:"var(--ink)" }}>{item.n}</span>
                <div style={{ display:"flex", gap:8, alignItems:"center" }}>
                  <span style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:10, color:"var(--muted)" }}>{item.m.toLocaleString()} M€</span>
                  <SBdg tone={item.ok?"g":"r"} label={item.ok?"✓ Couvert":"✗ Non couvert"}/>
                </div>
              </div>
              <div style={{ height:4, borderRadius:999, background:"rgba(30,50,38,.10)", overflow:"hidden" }}>
                <div style={{ height:"100%", width:`${Math.min(100,item.pct)}%`, background:item.ok?C.green:C.red, borderRadius:999 }}/>
              </div>
              <div style={{ fontSize:9, color:"var(--muted)", marginTop:3 }}>{item.pct.toFixed(1)}% couvert · ×{item.mult.toFixed(2)}</div>
            </div>
          ))}
        </DashSec>}
      </div>
    </div>
  );
}


/* ─── SCREENER ────────────────────────────────────────────────── */
/* Design system Invelps — 10 piliers analytiques, tooltips, pagination */

// Couleurs fonctionnelles screener
const scoreColor  = v => v>=75?C.green:v>=55?C.amber:C.red;
const mosColor    = v => v>=20?C.green:v>=5?C.amber:v>=-5?C.orange:C.red;
const cagrColor   = v => v>=15?C.green:v>=5?C.amber:C.red;
const ddColor     = v => v>-20?C.green:v>-35?C.amber:C.red;
const evaColor    = v => v>=5?C.green:v>=0?C.amber:C.red;
const erColor     = v => v>=3?C.green:v>=0?C.amber:C.red;
const signalTone  = s => ["BUY","ACCUMULATE","STRONG_BUY"].includes(s)?"pos":s==="SELL"||s==="AVOID"?"neg":"warn";

// Données mock screener (Top picks réels + titres issus du document)
const SCREENER_MOCK = [
  {symbol:"ONEGLOBAL.BO",company:"One Global Service Provider",sector:"Industrials",cap_class:"Nano Cap",moat:"Narrow",region:"Asia",country:"India",price:542,currency:"INR",ytd_pct:-25.66,iscore:75.9,dd_signal:"BUY",dd_global_score:84,dd_yes:4,dd_watch:2,dd_no:0,dd_summary:"Environnement de marché favorable. Signal: BUY.",dd_marche_label:"Marché favorable",dd_marche_status:"yes",dd_moat_label:"Moat Narrow - cycle 0Z",dd_moat_status:"watch",dd_value_label:"ROIC-WACC 27.9 pts",dd_value_status:"yes",dd_momentum_label:"TScore 64",dd_momentum_status:"yes",dd_rr_label:"Profil R/R acceptable",dd_rr_status:"watch",dd_valuation_label:"MOS 94.3%",dd_valuation_status:"yes",fund_score:91.7,fund_value_creation:92.5,fund_solvency:97.7,roic_vs_wacc:27.9,mos:94.3,fcf_yield:0,peg:0.02,tscore:64,tscore_label:"Momentum favorable",tsmom_3m:0,tsmom_6m:0,tsmom_12m:0,rsi14:50,timing_structure_tone:"green",rr_quadrant:"Inconnu",rr_dd_max:0,rr_sharpe:0,rr_irrs_score:0,bt_5y_cagr_tr:0,bt_5y_underperf:false,bt_5y_dd:0,dist_shareholder_yield:0,dist_style:"Dividend-led",dist_score:55,dist_sustainability:100,ef_cfo_coverage:0,ef_actionnaire_covered:false,er_proxy:4.62,er_reading:"Faible",top_driver:"la croissance",top_headwind:""},
  {symbol:"CMCX.L",company:"CMC Markets plc",sector:"Financials",cap_class:"Small Cap",moat:"Narrow",region:"Europe",country:"UK",price:347,currency:"GBp",ytd_pct:14.88,iscore:75.4,dd_signal:"BUY",dd_global_score:92,dd_yes:5,dd_watch:1,dd_no:0,dd_summary:"Marché favorable. Signal: BUY.",dd_marche_label:"Marché favorable",dd_marche_status:"yes",dd_moat_label:"Moat Narrow - cycle 0F",dd_moat_status:"watch",dd_value_label:"ROIC-WACC 14.0 pts",dd_value_status:"yes",dd_momentum_label:"TScore 55",dd_momentum_status:"yes",dd_rr_label:"Profil R/R attractif",dd_rr_status:"yes",dd_valuation_label:"MOS 55.2%",dd_valuation_status:"yes",fund_score:100,fund_value_creation:0,fund_solvency:88,roic_vs_wacc:14,mos:55.2,fcf_yield:0,peg:0.74,tscore:55,tscore_label:"Momentum favorable",tsmom_3m:0,tsmom_6m:0,tsmom_12m:0,rsi14:50,timing_structure_tone:"amber",rr_quadrant:"Inconnu",rr_dd_max:-73.15,rr_sharpe:5.48,rr_irrs_score:50,bt_5y_cagr_tr:36.5,bt_5y_underperf:false,bt_5y_dd:0,dist_shareholder_yield:6.92,dist_style:"Dividend-led",dist_score:72.1,dist_sustainability:100,ef_cfo_coverage:0,ef_actionnaire_covered:false,er_proxy:-5.4,er_reading:"Négatif",top_driver:"la croissance",top_headwind:""},
  {symbol:"NICL.JK",company:"PT PAM Mineral Tbk",sector:"Materials",cap_class:"Nano Cap",moat:"Narrow",region:"Southeast Asia",country:"Indonesia",price:850,currency:"IDR",ytd_pct:-33.33,iscore:75,dd_signal:"BUY",dd_global_score:84,dd_yes:4,dd_watch:2,dd_no:0,dd_summary:"Profil R/R attractif. Signal: BUY.",dd_marche_label:"Marché en transition",dd_marche_status:"watch",dd_moat_label:"Moat Narrow - cycle 0H1",dd_moat_status:"watch",dd_value_label:"ROIC-WACC 39.0 pts",dd_value_status:"yes",dd_momentum_label:"TScore 63",dd_momentum_status:"yes",dd_rr_label:"Profil R/R attractif",dd_rr_status:"yes",dd_valuation_label:"MOS 29.3%",dd_valuation_status:"yes",fund_score:89.7,fund_value_creation:88.6,fund_solvency:98.7,roic_vs_wacc:39,mos:29.3,fcf_yield:0,peg:0.01,tscore:63,tscore_label:"Momentum favorable",tsmom_3m:0,tsmom_6m:0,tsmom_12m:0,rsi14:50,timing_structure_tone:"green",rr_quadrant:"Inconnu",rr_dd_max:0,rr_sharpe:0.92,rr_irrs_score:50,bt_5y_cagr_tr:0,bt_5y_underperf:false,bt_5y_dd:0,dist_shareholder_yield:0,dist_style:"Dividend-led",dist_score:65.7,dist_sustainability:100,ef_cfo_coverage:0,ef_actionnaire_covered:false,er_proxy:4.96,er_reading:"Faible",top_driver:"la croissance",top_headwind:""},
  {symbol:"SMDR.JK",company:"PT Samudera Indonesia Tbk",sector:"Industrials",cap_class:"Nano Cap",moat:"Narrow",region:"Southeast Asia",country:"Indonesia",price:342,currency:"IDR",ytd_pct:-11.73,iscore:74.8,dd_signal:"BUY",dd_global_score:84,dd_yes:4,dd_watch:2,dd_no:0,dd_summary:"Marché favorable. Signal: BUY.",dd_marche_label:"Marché favorable",dd_marche_status:"yes",dd_moat_label:"Moat Narrow - cycle 0B1",dd_moat_status:"watch",dd_value_label:"ROIC-WACC 2.8 pts",dd_value_status:"yes",dd_momentum_label:"TScore 58",dd_momentum_status:"yes",dd_rr_label:"Profil R/R acceptable",dd_rr_status:"watch",dd_valuation_label:"MOS 999%",dd_valuation_status:"yes",fund_score:92.2,fund_value_creation:91,fund_solvency:93,roic_vs_wacc:2.8,mos:999,fcf_yield:0,peg:-0.16,tscore:58,tscore_label:"Momentum favorable",tsmom_3m:0,tsmom_6m:0,tsmom_12m:0,rsi14:50,timing_structure_tone:"amber",rr_quadrant:"Inconnu",rr_dd_max:-59.06,rr_sharpe:0.13,rr_irrs_score:50,bt_5y_cagr_tr:93,bt_5y_underperf:false,bt_5y_dd:0,dist_shareholder_yield:0,dist_style:"Dividend-led",dist_score:64.4,dist_sustainability:80,ef_cfo_coverage:0,ef_actionnaire_covered:false,er_proxy:-4.94,er_reading:"Négatif",top_driver:"la croissance",top_headwind:""},
  {symbol:"BLTA.JK",company:"PT Berlian Laju Tanker Tbk",sector:"Industrials",cap_class:"Nano Cap",moat:"Narrow",region:"Southeast Asia",country:"Indonesia",price:41,currency:"IDR",ytd_pct:2.38,iscore:74.6,dd_signal:"BUY",dd_global_score:84,dd_yes:4,dd_watch:2,dd_no:0,dd_summary:"Marché favorable. Signal: BUY.",dd_marche_label:"Marché favorable",dd_marche_status:"yes",dd_moat_label:"Moat Narrow - cycle 0D1",dd_moat_status:"watch",dd_value_label:"ROIC-WACC 1.0 pts",dd_value_status:"yes",dd_momentum_label:"TScore 57",dd_momentum_status:"yes",dd_rr_label:"Profil R/R acceptable",dd_rr_status:"watch",dd_valuation_label:"MOS 97.9%",dd_valuation_status:"yes",fund_score:89.3,fund_value_creation:85.2,fund_solvency:81.3,roic_vs_wacc:1,mos:97.9,fcf_yield:0,peg:-0.16,tscore:57,tscore_label:"Momentum favorable",tsmom_3m:0,tsmom_6m:0,tsmom_12m:0,rsi14:50,timing_structure_tone:"amber",rr_quadrant:"Inconnu",rr_dd_max:-98.87,rr_sharpe:0,rr_irrs_score:50,bt_5y_cagr_tr:0,bt_5y_underperf:false,bt_5y_dd:0,dist_shareholder_yield:0,dist_style:"Buyback-led",dist_score:29.6,dist_sustainability:35,ef_cfo_coverage:0,ef_actionnaire_covered:false,er_proxy:-4.94,er_reading:"Négatif",top_driver:"la croissance",top_headwind:""},
  {symbol:"7191.T",company:"Entrust Inc.",sector:"Financials",cap_class:"Nano Cap",moat:"Narrow",region:"Asia",country:"Japan",price:1096,currency:"JPY",ytd_pct:-9.79,iscore:74.2,dd_signal:"BUY",dd_global_score:84,dd_yes:4,dd_watch:2,dd_no:0,dd_summary:"Marché favorable. Signal: BUY.",dd_marche_label:"Marché favorable",dd_marche_status:"yes",dd_moat_label:"Moat Narrow - cycle 0F",dd_moat_status:"watch",dd_value_label:"ROIC-WACC 25.0 pts",dd_value_status:"yes",dd_momentum_label:"TScore 54",dd_momentum_status:"yes",dd_rr_label:"Profil R/R acceptable",dd_rr_status:"watch",dd_valuation_label:"MOS 63.9%",dd_valuation_status:"yes",fund_score:97.5,fund_value_creation:0,fund_solvency:85,roic_vs_wacc:25,mos:63.9,fcf_yield:0,peg:0.64,tscore:54,tscore_label:"Momentum favorable",tsmom_3m:0,tsmom_6m:0,tsmom_12m:0,rsi14:50,timing_structure_tone:"amber",rr_quadrant:"Inconnu",rr_dd_max:0,rr_sharpe:0,rr_irrs_score:50,bt_5y_cagr_tr:11.9,bt_5y_underperf:false,bt_5y_dd:0,dist_shareholder_yield:2.53,dist_style:"Dividend-led",dist_score:80.1,dist_sustainability:100,ef_cfo_coverage:0,ef_actionnaire_covered:false,er_proxy:-2.01,er_reading:"Négatif",top_driver:"la croissance",top_headwind:""},
  {symbol:"KAP.IL",company:"Kazatomprom",sector:"Energy",cap_class:"Mega Cap",moat:"Narrow",region:"Central Asia",country:"Kazakhstan",price:80.3,currency:"USD",ytd_pct:39.78,iscore:74.2,dd_signal:"BUY",dd_global_score:84,dd_yes:4,dd_watch:2,dd_no:0,dd_summary:"Marché favorable. Signal: BUY.",dd_marche_label:"Marché favorable",dd_marche_status:"yes",dd_moat_label:"Moat Narrow - cycle 0B1",dd_moat_status:"watch",dd_value_label:"ROIC-WACC 18.7 pts",dd_value_status:"yes",dd_momentum_label:"TScore 60",dd_momentum_status:"yes",dd_rr_label:"Profil R/R acceptable",dd_rr_status:"watch",dd_valuation_label:"MOS 63.6%",dd_valuation_status:"yes",fund_score:97.6,fund_value_creation:0,fund_solvency:90.7,roic_vs_wacc:18.7,mos:63.6,fcf_yield:0,peg:0.05,tscore:60,tscore_label:"Momentum favorable",tsmom_3m:0,tsmom_6m:0,tsmom_12m:0,rsi14:50,timing_structure_tone:"green",rr_quadrant:"Inconnu",rr_dd_max:0,rr_sharpe:0,rr_irrs_score:50,bt_5y_cagr_tr:48.5,bt_5y_underperf:false,bt_5y_dd:0,dist_shareholder_yield:0,dist_style:"Dividend-led",dist_score:72.8,dist_sustainability:100,ef_cfo_coverage:0,ef_actionnaire_covered:false,er_proxy:4.64,er_reading:"Faible",top_driver:"la croissance",top_headwind:""},
  {symbol:"TSM",company:"Taiwan Semiconductor",sector:"Information Technology",cap_class:"Mega Cap",moat:"Wide",region:"Asia",country:"USA",price:339,currency:"USD",ytd_pct:11.21,iscore:72.3,dd_signal:"BUY",dd_global_score:92,dd_yes:5,dd_watch:1,dd_no:0,dd_summary:"Avantage concurrentiel confirmé. Signal: BUY.",dd_marche_label:"Marché favorable",dd_marche_status:"yes",dd_moat_label:"Wide moat confirmé",dd_moat_status:"yes",dd_value_label:"ROIC-WACC 14.8 pts",dd_value_status:"yes",dd_momentum_label:"TScore 59",dd_momentum_status:"yes",dd_rr_label:"Profil R/R acceptable",dd_rr_status:"watch",dd_valuation_label:"MOS 92.7%",dd_valuation_status:"yes",fund_score:94.2,fund_value_creation:92.5,fund_solvency:97.7,roic_vs_wacc:14.8,mos:92.7,fcf_yield:0,peg:0.81,tscore:59,tscore_label:"Momentum favorable",tsmom_3m:0,tsmom_6m:0,tsmom_12m:0,rsi14:50,timing_structure_tone:"amber",rr_quadrant:"Inconnu",rr_dd_max:0,rr_sharpe:0,rr_irrs_score:50,bt_5y_cagr_tr:33.9,bt_5y_underperf:false,bt_5y_dd:0,dist_shareholder_yield:1.09,dist_style:"Dividend-led",dist_score:69.1,dist_sustainability:100,ef_cfo_coverage:0,ef_actionnaire_covered:false,er_proxy:-1.95,er_reading:"Négatif",top_driver:"la croissance",top_headwind:""},
  {symbol:"NVDA",company:"NVIDIA Corporation",sector:"Information Technology",cap_class:"Mega Cap",moat:"Wide",region:"North America",country:"USA",price:177.4,currency:"USD",ytd_pct:-6.49,iscore:71.5,dd_signal:"BUY",dd_global_score:78.3,dd_yes:4,dd_watch:1,dd_no:1,dd_summary:"Moat confirmé. Valorisation tendue. Signal: BUY.",dd_marche_label:"Marché favorable",dd_marche_status:"yes",dd_moat_label:"Wide moat confirmé",dd_moat_status:"yes",dd_value_label:"ROIC-WACC 44.9 pts",dd_value_status:"yes",dd_momentum_label:"TScore 54",dd_momentum_status:"watch",dd_rr_label:"Profil R/R attractif",dd_rr_status:"yes",dd_valuation_label:"MOS -26.2%",dd_valuation_status:"no",fund_score:100,fund_value_creation:0,fund_solvency:88,roic_vs_wacc:44.9,mos:-26.2,fcf_yield:0,peg:0.09,tscore:54,tscore_label:"Momentum en transition",tsmom_3m:0,tsmom_6m:0,tsmom_12m:0,rsi14:50,timing_structure_tone:"amber",rr_quadrant:"Inconnu",rr_dd_max:0,rr_sharpe:0,rr_irrs_score:50,bt_5y_cagr_tr:90.3,bt_5y_underperf:false,bt_5y_dd:0,dist_shareholder_yield:0.64,dist_style:"Buyback-led",dist_score:70.2,dist_sustainability:100,ef_cfo_coverage:0,ef_actionnaire_covered:false,er_proxy:-2.39,er_reading:"Négatif",top_driver:"la croissance",top_headwind:""},
  {symbol:"AZN",company:"AstraZeneca PLC",sector:"Health Care",cap_class:"Mega Cap",moat:"Narrow",region:"Europe",country:"UK",price:203.5,currency:"USD",ytd_pct:114.53,iscore:72.2,dd_signal:"BUY",dd_global_score:84,dd_yes:4,dd_watch:2,dd_no:0,dd_summary:"Marché favorable. Signal: BUY.",dd_marche_label:"Marché favorable",dd_marche_status:"yes",dd_moat_label:"Moat Narrow - cycle PH3a",dd_moat_status:"watch",dd_value_label:"ROIC-WACC 8.0 pts",dd_value_status:"yes",dd_momentum_label:"TScore 66",dd_momentum_status:"yes",dd_rr_label:"Profil R/R acceptable",dd_rr_status:"watch",dd_valuation_label:"MOS 21.4%",dd_valuation_status:"yes",fund_score:82,fund_value_creation:82.5,fund_solvency:80,roic_vs_wacc:8,mos:21.4,fcf_yield:0,peg:-0.71,tscore:66,tscore_label:"Momentum favorable",tsmom_3m:0,tsmom_6m:0,tsmom_12m:0,rsi14:50,timing_structure_tone:"green",rr_quadrant:"Inconnu",rr_dd_max:0,rr_sharpe:0,rr_irrs_score:50,bt_5y_cagr_tr:18.5,bt_5y_underperf:false,bt_5y_dd:0,dist_shareholder_yield:2.26,dist_style:"Dividend-led",dist_score:71.7,dist_sustainability:80,ef_cfo_coverage:0,ef_actionnaire_covered:false,er_proxy:4.83,er_reading:"Faible",top_driver:"la croissance",top_headwind:""},
];

const SC_SECTORS  = ["Tous", ...new Set(SCREENER_MOCK.map(s=>s.sector))].sort((a,b)=>a==="Tous"?-1:a.localeCompare(b));
const SC_MOATS    = ["Tous","Wide","Narrow","None"];
const SC_SIGNALS  = ["Tous","BUY","STRONG_BUY","ACCUMULATE","HOLD","SELL","REDUCE"];
const SC_CAPS     = ["Tous","Mega Cap","Large Cap","Mid Cap","Small Cap","Nano Cap"];
const SC_REGIONS  = ["Tous",...new Set(SCREENER_MOCK.map(s=>s.region))].sort((a,b)=>a==="Tous"?-1:a.localeCompare(b));
const SC_DIST_ST  = ["Tous","Dividend-led","Buyback-led","Balanced"];
const SC_STRUCT   = ["Tous","green","amber","red"];
const SC_MARCHE   = ["Tous","yes","watch","no"];

const SC_PILIERS = [
  { id:"profil", label:"Profil", icon:"◆", filters:[
    { id:"sc_sig",    label:"Signal décision",    type:"pills", options:SC_SIGNALS, field:"dd_signal",
      tt:"Signal synthétique issu des 6 facteurs. BUY = ≥4 facteurs favorables. HOLD = équilibre. SELL = ≥3 défavorables. Source : StockDashboard_Decisions.dd_signal" },
    { id:"sc_moat",   label:"Moat",               type:"pills", options:SC_MOATS,   field:"moat",
      tt:"Avantage concurrentiel durable. Wide = barrière forte (pricing power, switching costs). Narrow = avantage réel mais limité. None = sans protection structurelle. Source : StockDashboard.moat" },
    { id:"sc_cap",    label:"Capitalisation",      type:"pills", options:SC_CAPS,    field:"cap_class",
      tt:"Taille par capitalisation boursière. Mega >200Mds$. Large 10-200Mds$. Mid 2-10Mds$. Small 300M-2Mds$. Nano <300M$. Source : StockDashboard.market_cap_class" },
    { id:"sc_region", label:"Région",              type:"pills", options:SC_REGIONS, field:"region",
      tt:"Zone géographique du siège ou de la cotation principale. Cohérent avec la vue Market Flow. Source : StockDashboard.region" },
    { id:"sc_sector", label:"Secteur GICS",        type:"pills", options:SC_SECTORS, field:"sector",
      tt:"Classification sectorielle GICS (11 secteurs). Combinez avec Moat et structure de marché. Source : StockDashboard.sector" },
  ]},
  { id:"decision", label:"Facteurs Décision", icon:"⊕", filters:[
    { id:"sc_iscore", label:"IScore min",          type:"range", min:0,   max:100, field:"iscore",          step:1,
      tt:"Score composite Invelps /100 : Fondamentaux 25%, Valorisation 20%, Marché 15%, Momentum 20%, R/R 20%. ≥75 = conviction forte. ≥50 = univers élargi. Source : StockDashboard_Decisions.iscore" },
    { id:"sc_yes",    label:"Facteurs fav. min",   type:"range", min:0,   max:6,   field:"dd_yes_count",    step:1,
      tt:"Nombre de facteurs (sur 6) signalant positivement : marché, moat, création de valeur, momentum, R/R, valorisation. ≥4 = forte convergence. Source : StockDashboard_Decisions.dd_yes_count" },
    { id:"sc_score",  label:"Score global min",    type:"range", min:0,   max:100, field:"dd_global_score", step:1,
      tt:"Score global décisionnel /100 calculé par le Decision Engine. ≥75 = profil solide. ≥55 = acceptable. Source : StockDashboard_Decisions.dd_global_score" },
  ]},
  { id:"fondamentaux", label:"Fondamentaux", icon:"■", filters:[
    { id:"sc_fund",   label:"Score fondamental min",      type:"range", min:0,   max:100, field:"fund_score",              step:1,
      tt:"Agrégation de 9 piliers : liquidité, profitabilité, croissance, efficacité, rentabilité, cash-flow, solvabilité, allocation capital, création de valeur. ≥65 = solides. Source : StockDashboard.fund_score" },
    { id:"sc_eva_s",  label:"Score création valeur min",  type:"range", min:0,   max:100, field:"fund_value_creation_score", step:1,
      tt:"Score EVA basé sur ROIC vs WACC. ≥60 = création structurelle. <30 = EVA négative. Source : StockDashboard.fund_value_creation_score" },
    { id:"sc_solv",   label:"Solvabilité min",             type:"range", min:0,   max:100, field:"fund_solvency_score",       step:1,
      tt:"Solidité du bilan : levier, couverture intérêts, autonomie bilancielle. ≥60 pour limiter le risque en cycle baissier. Source : StockDashboard.fund_solvency_score" },
  ]},
  { id:"valorisation", label:"Valorisation", icon:"○", filters:[
    { id:"sc_mos",    label:"MOS min (%)",              type:"range", min:-100, max:200, field:"mos_pct",      step:5,
      tt:"Marge de sécurité = (Valeur DCF - Prix) / Prix × 100. MOS >0 = décote. MOS <0 = surévaluation. Graham recommandait ≥25%. Source : StockDashboard.Margin_of_safety × 100" },
    { id:"sc_fcf",    label:"FCF Yield min (%)",         type:"range", min:0,    max:20,  field:"fcf_yield_pct", step:1,
      tt:"Free Cash Flow / Capitalisation × 100. Rendement réel de trésorerie pour l'actionnaire. ≥4% = attractif. Source : StockDashboard.fcf_yield × 100" },
    { id:"sc_eva",    label:"EVA ROIC-WACC min (pts)",   type:"range", min:-20,  max:60,  field:"roic_vs_wacc",  step:1,
      tt:"ROIC - WACC en points. Positif = création de valeur économique. ≥5 pts = création forte. <0 = destruction de valeur. Source : StockDashboard.roic_vs_wacc_val" },
  ]},
  { id:"marche", label:"Structure de Marché", icon:"◉", filters:[
    { id:"sc_struct", label:"Tonalité structure",  type:"pills", options:SC_STRUCT,  field:"timing_structure_tone",
      tt:"Tonalité technique du prix. green = haussier confirmé. amber = transition mixte. red = structure dégradée. Source : StockDashboard.timing_structure_tone" },
    { id:"sc_mstat",  label:"Signal marché",       type:"pills", options:SC_MARCHE,  field:"dd_marche_status",
      tt:"Évaluation macro/sectorielle. yes = favorable. watch = neutre/transition. no = défavorable. Source : StockDashboard_Decisions.dd_marche_status" },
  ]},
  { id:"timing", label:"Timing", icon:"◎", filters:[
    { id:"sc_ts",     label:"TScore min",          type:"range", min:0,   max:100, field:"tscore",    step:1,
      tt:"Score technique composite : tendance, momentum, structure, volumes, SMA. ≥60 = momentum favorable. <40 = pression vendeuse. Indicateur de timing d'entrée. Source : StockDashboard.tscore" },
    { id:"sc_mom3m",  label:"Momentum 3M min (%)", type:"range", min:-40, max:40,  field:"tsmom_3m",  step:1,
      tt:"Performance relative sur 3 mois. Persistance statistique jusqu'à 12 mois (Ilmanen/AQR). Éviter <-15% sauf catalyseur. Source : StockDashboard.tsmom_3m" },
    { id:"sc_rsi",    label:"RSI 14 min",           type:"range", min:0,   max:100, field:"rsi_14",    step:1,
      tt:"Relative Strength Index sur 14 périodes. <30 = survente. >70 = surachat. 40-60 = zone neutre. Source : StockDashboard.rsi_14" },
  ]},
  { id:"rr", label:"Risque / Rendement", icon:"◇", filters:[
    { id:"sc_cagr",   label:"CAGR TR 5Y min (%)",  type:"range", min:-20, max:100, field:"bt_5y_cagr_tr",   step:1,
      tt:"CAGR Total Return sur 5 ans (prix + dividendes réinvestis). Seuil ≥8-10% = couvre le coût du capital. Source : StockDashboard_Capital.bt_5y_cagr_tr_pct" },
    { id:"sc_dd",     label:"DD Max ≥ (%)",         type:"range", min:-100, max:0,  field:"rr_max_drawdown", step:5,
      tt:"Perte maximale depuis un plus haut sur la période. ≤-30% pour profils prudents. Source : StockDashboard.rr_max_drawdown_weighted" },
    { id:"sc_sharpe", label:"Sharpe min",            type:"range", min:-2,   max:5,  field:"rr_sharpe",       step:0.1,
      tt:"Excès de rendement par unité de volatilité. >1 = excellent. 0.5-1 = acceptable. <0 = sous le taux sans risque. Source : StockDashboard.rr_sharpe_weighted" },
  ]},
  { id:"ef", label:"État Financier", icon:"≡", filters:[
    { id:"sc_cfo",    label:"Couverture CFO min (%)", type:"range", min:0, max:110, field:"ef_wf_coverage_pct", step:5,
      tt:"% des besoins couverts par le CFO (capex + dette + actionnaires + BFR). <25% = dépendance externe. ≥50% = confort. >100% = générateur net de cash. Source : StockDashboard.ef_wf_coverage_pct" },
  ]},
  { id:"backtest", label:"Backtest", icon:"⊞", filters:[
    { id:"sc_bt3y",   label:"CAGR TR 3Y min (%)", type:"range", min:-20, max:100, field:"bt_3y_cagr_tr", step:1,
      tt:"CAGR Total Return sur 3 ans. Plus récent que le 5Y. Source : StockDashboard_Capital.bt_3y_cagr_tr_pct" },
    { id:"sc_bt1y",   label:"CAGR TR 1Y min (%)", type:"range", min:-50, max:200, field:"bt_1y_cagr_tr", step:5,
      tt:"Performance totale sur 1 an. Volatile — à croiser avec le 3Y et 5Y pour évaluer la consistance. Source : StockDashboard_Capital.bt_1y_cagr_tr_pct" },
  ]},
  { id:"distribution", label:"Distribution", icon:"⁙", filters:[
    { id:"sc_shy",    label:"Shareholder Yield min (%)", type:"range", min:0, max:20, field:"dist_shareholder_yield", step:1,
      tt:"Dividende yield + buyback yield = retour total à l'actionnaire. ≥4% pour profil income. Source : StockDashboard_Capital.dist_shareholder_yield" },
    { id:"sc_dst",    label:"Style distribution", type:"pills", options:SC_DIST_ST, field:"dist_style",
      tt:"Mode de rémunération. Dividend-led = dividendes réguliers. Buyback-led = rachats. Balanced = mix. Source : StockDashboard_Capital.dist_style" },
  ]},
];

function ScPill({ options, value, onChange }) {
  return (
    <div style={{ display:"flex", gap:4, flexWrap:"wrap" }}>
      {options.map(o => {
        const a = value===o;
        return <button key={o} onClick={()=>onChange(o)} style={{
          padding:"3px 9px", fontSize:10, borderRadius:4, cursor:"pointer",
          fontFamily:"'IBM Plex Mono',monospace", whiteSpace:"nowrap", border:`1px solid ${a?C.amber+"88":"var(--line)"}`,
          background:a?"rgba(202,168,94,.14)":"transparent",
          color:a?C.amber:"var(--muted)", fontWeight:800,
        }}>{o}</button>;
      })}
    </div>
  );
}

function ScSlider({ f, value, onChange }) {
  const { min, max, step=1 } = f;
  const pct = ((value-min)/(max-min))*100;
  return (
    <div>
      <div style={{ display:"flex", justifyContent:"space-between", marginBottom:4 }}>
        <span style={{ fontSize:9, color:"var(--muted)", fontFamily:"'IBM Plex Mono',monospace", opacity:.6 }}>{min}</span>
        <span style={{ fontSize:12, color:C.amber, fontFamily:"'IBM Plex Mono',monospace", fontWeight:800 }}>≥ {value}</span>
        <span style={{ fontSize:9, color:"var(--muted)", fontFamily:"'IBM Plex Mono',monospace", opacity:.6 }}>{max}</span>
      </div>
      <div style={{ position:"relative", height:18, display:"flex", alignItems:"center" }}>
        <div style={{ position:"absolute", inset:"7px 0", height:4, background:"var(--line)", borderRadius:2 }}/>
        <div style={{ position:"absolute", left:0, width:`${pct}%`, top:"7px", height:4, background:`${C.amber}66`, borderRadius:2 }}/>
        <input type="range" min={min} max={max} step={step} value={value} onChange={e=>onChange(Number(e.target.value))}
          style={{ position:"absolute", inset:0, width:"100%", opacity:0, height:"100%", cursor:"pointer" }}/>
        <div style={{ position:"absolute", left:`${pct}%`, top:"50%", transform:"translate(-50%,-50%)", width:13, height:13, borderRadius:"50%", background:C.amber, border:"2px solid var(--paper)", pointerEvents:"none", zIndex:2 }}/>
      </div>
    </div>
  );
}

function ScDecisionTooltip({ stock, th }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const fn = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", fn);
    return () => document.removeEventListener("mousedown", fn);
  }, [open]);

  const dot = (st) => ({ yes:C.green, watch:C.amber, no:C.red })[st]||C.amber;

  return (
    <div ref={ref} style={{ position:"relative" }}>
      <button onClick={e=>{e.stopPropagation();setOpen(o=>!o);}}
        style={{ background:"none", border:"none", cursor:"pointer", padding:0, display:"flex", flexDirection:"column", gap:3 }}>
        <span style={{ fontSize:8, fontWeight:800, padding:"2px 6px", borderRadius:3,
          background:signalTone(stock.dd_signal)==="pos"?`${C.green}18`:signalTone(stock.dd_signal)==="neg"?`${C.red}18`:`${C.amber}18`,
          border:`1px solid ${signalTone(stock.dd_signal)==="pos"?C.green:signalTone(stock.dd_signal)==="neg"?C.red:C.amber}44`,
          color:signalTone(stock.dd_signal)==="pos"?C.green:signalTone(stock.dd_signal)==="neg"?C.red:C.amber,
          fontFamily:"'IBM Plex Mono',monospace" }}>{stock.dd_signal}</span>
        <div style={{ display:"flex", gap:2 }}>
          {Array.from({length:stock.dd_yes}).map((_,j)=><span key={"y"+j} style={{width:5,height:5,borderRadius:2,background:C.green}}/>)}
          {Array.from({length:stock.dd_watch}).map((_,j)=><span key={"w"+j} style={{width:5,height:5,borderRadius:2,background:C.amber}}/>)}
          {Array.from({length:stock.dd_no}).map((_,j)=><span key={"n"+j} style={{width:5,height:5,borderRadius:2,background:C.red}}/>)}
        </div>
      </button>
      {open && (
        <div style={{ position:"absolute", top:"100%", left:0, zIndex:200, width:260, borderRadius:10,
          border:"1px solid var(--line)", background:"var(--paper)", padding:12, boxShadow:"0 8px 28px rgba(0,0,0,.18)" }}>
          <div style={{ fontSize:10, fontWeight:800, color:C.amber, marginBottom:8, fontFamily:"'IBM Plex Mono',monospace" }}>◈ FACTEURS DÉCISION</div>
          {[["Marché",stock.dd_marche_status,stock.dd_marche_label],["Moat",stock.dd_moat_status,stock.dd_moat_label],["Valeur",stock.dd_value_status,stock.dd_value_label],["Momentum",stock.dd_momentum_status,stock.dd_momentum_label],["R/R",stock.dd_rr_status,stock.dd_rr_label],["Valorisation",stock.dd_valuation_status,stock.dd_valuation_label]].map(([l,st,d])=>(
            <div key={l} style={{ display:"flex", gap:6, marginBottom:5, alignItems:"flex-start" }}>
              <span style={{ width:8, height:8, borderRadius:2, background:dot(st), flexShrink:0, marginTop:3 }}/>
              <div style={{ fontSize:10, color:"var(--ink)", lineHeight:1.5 }}><strong>{l}</strong> — {d}</div>
            </div>
          ))}
          <div style={{ marginTop:6, padding:"5px 7px", borderRadius:6, background:"var(--paper-alt)", fontSize:10, color:"var(--muted)", fontStyle:"italic" }}>{stock.dd_summary}</div>
        </div>
      )}
    </div>
  );
}

const SC_COLS = [
  { key:null,            label:"Titre",    w:148 },
  { key:"dd_signal",     label:"Signal",   w:80  },
  { key:"iscore",        label:"IScore",   w:64  },
  { key:"fund_score",    label:"Fund",     w:56  },
  { key:"roic_vs_wacc",  label:"EVA",      w:60  },
  { key:"mos",           label:"MOS",      w:70  },
  { key:"tscore",        label:"TScore",   w:60  },
  { key:"bt_5y_cagr_tr", label:"CAGR5Y",  w:66  },
  { key:"er_proxy",      label:"ER AQR",   w:62  },
  { key:"ytd_pct",       label:"YTD",      w:56  },
  { key:"moat",          label:"Moat",     w:60  },
];

function ScreenerPage({ nav, th, openTicker }) {
  const [rows, setRows] = useState(SCREENER_MOCK);
  const [panelOpen, setPanel] = useState(false); // fermé par défaut sur mobile
  const [sk, setSk] = useState("iscore");
  const [sd, setSd] = useState("desc");
  const [page, setPage] = useState(0);
  const [isMobile, setMobile] = useState(false);
  const [openPiliers, setOpenPiliers] = useState([]); // toutes collapsed par défaut
  const PAGE = 20;

  useEffect(() => {
    const fn = () => setMobile(window.innerWidth < 768);
    fn(); window.addEventListener("resize", fn);
    return () => window.removeEventListener("resize", fn);
  }, []);

  useEffect(() => {
    const base = (Constants.BACKEND_SERVER_BASE_URL || "").replace(/\/$/, "");
    if (!base) return;
    fetch(`${base}/capitalflow/screener`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
      .then((j) => {
        if (Array.isArray(j.rows) && j.rows.length) setRows(j.rows);
      })
      .catch(() => {});
  }, []);

  const filterPiliers = useMemo(() => {
    const sectors = ["Tous", ...new Set(rows.map((s) => s.sector).filter(Boolean))].sort((a, b) => (a === "Tous" ? -1 : a.localeCompare(b)));
    const regions = ["Tous", ...new Set(rows.map((s) => s.region).filter(Boolean))].sort((a, b) => (a === "Tous" ? -1 : a.localeCompare(b)));
    return SC_PILIERS.map((p) => ({
      ...p,
      filters: p.filters.map((f) => {
        if (f.id === "sc_sector") return { ...f, options: sectors };
        if (f.id === "sc_region") return { ...f, options: regions };
        return f;
      }),
    }));
  }, [rows]);

  // Sur desktop, panel ouvert par défaut
  useEffect(() => { if (!isMobile) setPanel(true); }, [isMobile]);

  const defFilters = useMemo(() => {
    const d = {};
    SC_PILIERS.forEach((p) =>
      p.filters.forEach((f) => {
        d[f.id] = f.type === "pills" ? "Tous" : f.min;
      })
    );
    return d;
  }, []);
  const [fv, setFvAll] = useState(defFilters);
  const setFV = (id,v) => setFvAll(p=>({...p,[id]:v}));

  const ts = k => { if(sk===k) setSd(d=>d==="desc"?"asc":"desc"); else { setSk(k); setSd("desc"); } };

  // Normalise les champs mock → noms réels SQL
  // Permet d'utiliser les vrais noms de colonnes dans SC_PILIERS.field
  // sans casser le mock data existant
  const getField = (s, field) => {
    const map = {
      // SQL field name          → mock field name
      "dd_yes_count":            s.dd_yes ?? s.dd_yes_count ?? 0,
      "dd_global_score":         s.dd_global_score ?? 0,
      "fund_value_creation_score": s.fund_value_creation ?? s.fund_value_creation_score ?? 0,
      "fund_solvency_score":     s.fund_solvency ?? s.fund_solvency_score ?? 0,
      "mos_pct":                 s.mos ?? s.mos_pct ?? 0,
      "fcf_yield_pct":           s.fcf_yield_pct ?? (s.fcf_yield != null && Math.abs(s.fcf_yield) < 1 ? s.fcf_yield * 100 : s.fcf_yield) ?? 0,
      "roic_vs_wacc":            s.roic_vs_wacc ?? s.roic_vs_wacc_val ?? 0,
      "rsi_14":                  s.rsi14 ?? s.rsi_14 ?? 50,
      "rr_max_drawdown":         s.rr_dd_max ?? s.rr_max_drawdown ?? 0,
      "rr_sharpe":               s.rr_sharpe ?? s.rr_sharpe_weighted ?? 0,
      "bt_5y_cagr_tr":           s.bt_5y_cagr_tr ?? s.bt_5y_cagr_tr_pct ?? 0,
      "bt_3y_cagr_tr":           s.bt_3y_cagr_tr ?? s.bt_3y_cagr_tr_pct ?? 0,
      "bt_1y_cagr_tr":           s.bt_1y_cagr_tr ?? s.bt_1y_cagr_tr_pct ?? 0,
      "ef_wf_coverage_pct":      s.ef_cfo_coverage ?? s.ef_wf_coverage_pct ?? 0,
      "tsmom_3m":                s.tsmom_3m ?? 0,
    };
    return field in map ? map[field] : (s[field] ?? 0);
  };

  const filtered = useMemo(() => {
    return rows.filter((s) => {
      for (const pilier of filterPiliers) {
        for (const f of pilier.filters) {
          const val = fv[f.id];
          if (f.type==="pills") {
            if (val !== "Tous" && s[f.field] !== val) return false;
          } else {
            const sv = getField(s, f.field);
            if (sv < val) return false;
          }
        }
      }
      return true;
    }).sort((a,b) => {
      const av = getField(a, sk), bv = getField(b, sk);
      return sd==="desc" ? bv-av : av-bv;
    });
  }, [fv, sk, sd, rows, filterPiliers]);

  useEffect(()=>setPage(0), [filtered]);
  const totalPages = Math.ceil(filtered.length/PAGE);
  const paginated  = filtered.slice(page*PAGE, (page+1)*PAGE);

  const activeCount = useMemo(() => {
    let n = 0;
    SC_PILIERS.forEach((p) =>
      p.filters.forEach((f) => {
        const v = fv[f.id];
        const def = f.type === "pills" ? "Tous" : f.min;
        if (v !== def) n++;
      })
    );
    return n;
  }, [fv]);

  return (
    <div style={{ maxWidth:1560, margin:"0 auto" }}>
      {/* Sub-header */}
      <div style={{ position:"sticky", top:52, zIndex:39, background:"var(--paper)", borderBottom:"1px solid var(--line)", backdropFilter:"blur(14px)", padding:"7px 14px", display:"flex", alignItems:"center", justifyContent:"space-between", gap:8, flexWrap:"wrap" }}>
        <div style={{ display:"flex", alignItems:"center", gap:8 }}>
          <span style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:12, fontWeight:700, color:"var(--ink)" }}>SCREENER</span>
          {activeCount>0&&<span style={{ fontSize:10, padding:"2px 8px", borderRadius:999, background:`${C.amber}18`, border:`1px solid ${C.amber}44`, color:C.amber, fontWeight:700 }}>{activeCount} filtre{activeCount>1?"s":""}</span>}
        </div>
        <div style={{ display:"flex", gap:8, alignItems:"center" }}>
          <span style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:11, color:"var(--muted)" }}><span style={{color:C.amber,fontWeight:700}}>{filtered.length}</span>/{rows.length}</span>
          <button onClick={()=>setPanel(p=>!p)} style={{ padding:"5px 11px", fontSize:11, border:`1px solid ${panelOpen?C.amber+"66":"var(--line)"}`, background:panelOpen?`${C.amber}12`:"var(--paper-alt)", color:panelOpen?C.amber:"var(--muted)", borderRadius:8, fontWeight:700 }}>
            ⊟ {panelOpen?"Masquer":"Filtres"}{activeCount>0?` (${activeCount})`:""}
          </button>
          <button onClick={()=>setFvAll(defFilters)} style={{ padding:"5px 10px", fontSize:11, border:`1px solid ${C.red}44`, background:`${C.red}0e`, color:C.red, borderRadius:8, fontWeight:700 }}>↺</button>
        </div>
      </div>

      <div style={{ padding:"12px 14px 40px", display:"flex", flexDirection: isMobile?"column":"row", gap:14 }}>
        {/* Sidebar filtres — accordéon mobile, fixe desktop */}
        {panelOpen && (
          <div style={{ width: isMobile?"100%":252, flexShrink:0 }}>
            {/* Header sidebar — desktop */}
            {!isMobile && (
              <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:8, padding:"0 2px" }}>
                <span style={{ fontSize:9, fontFamily:"'IBM Plex Mono',monospace", color:"var(--muted)", letterSpacing:".10em", textTransform:"uppercase" }}>Filtres</span>
                <div style={{ display:"flex", gap:5 }}>
                  <button onClick={()=>setOpenPiliers(openPiliers.length===filterPiliers.length?[]:filterPiliers.map(p=>p.id))}
                    style={{ fontSize:9, padding:"2px 8px", border:"1px solid var(--line)", background:"var(--paper-alt)", color:"var(--muted)", borderRadius:4, cursor:"pointer" }}>
                    {openPiliers.length===filterPiliers.length?"▲ Réduire":"▼ Ouvrir"}
                  </button>
                  <button onClick={()=>setFvAll(defFilters)} style={{ fontSize:9, padding:"2px 8px", border:`1px solid ${C.red}44`, background:`${C.red}10`, color:C.red, borderRadius:4, cursor:"pointer" }}>↺ Reset</button>
                </div>
              </div>
            )}
            {/* Header accordéon — mobile */}
            {isMobile && (
              <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:8 }}>
                <button onClick={()=>setOpenPiliers(openPiliers.length===filterPiliers.length?[]:filterPiliers.map(p=>p.id))}
                  style={{ flex:1, padding:"10px 14px", borderRadius:10, border:`1px solid ${C.amber}44`, background:`${C.amber}0e`, color:C.amber, fontSize:12, fontWeight:800, cursor:"pointer", marginRight:8 }}>
                  {openPiliers.length===filterPiliers.length?"▲ Réduire tous":"▼ Ouvrir tous"}
                </button>
                <button onClick={()=>setFvAll(defFilters)} style={{ padding:"10px 14px", borderRadius:10, border:`1px solid ${C.red}44`, background:`${C.red}0e`, color:C.red, fontSize:12, fontWeight:700, cursor:"pointer" }}>↺</button>
              </div>
            )}
            {/* Accordéon 10 piliers */}
            <div style={{ display:"flex", flexDirection:"column", gap:isMobile?8:6, maxHeight:isMobile?undefined:"calc(100vh - 160px)", overflowY:isMobile?undefined:"auto", paddingRight:isMobile?0:2 }}>
              {filterPiliers.map(pilier => {
                const isOpen = openPiliers.includes(pilier.id);
                // Compter filtres actifs dans ce pilier
                const active = pilier.filters.filter(f=>{
                  const v=fv[f.id]; const def=f.type==="pills"?"Tous":f.min;
                  return v!==def;
                }).length;
                return (
                  <div key={pilier.id} style={{ borderRadius:12, border:`1px solid ${isOpen?"rgba(202,168,94,.35)":"var(--line2)"}`, background:"var(--paper)", boxShadow:"0 2px 8px rgba(0,0,0,.05)", overflow:"hidden" }}>
                    {/* Row header — 56px height */}
                    <button onClick={()=>setOpenPiliers(p=>p.includes(pilier.id)?p.filter(x=>x!==pilier.id):[...p,pilier.id])}
                      style={{ width:"100%", height:56, display:"flex", alignItems:"center", gap:12, padding:"0 16px", background:"none", border:"none", cursor:"pointer", textAlign:"left" }}>
                      {/* Icône colorée */}
                      <div style={{ width:32, height:32, borderRadius:8, background:`${C.amber}18`, border:`1px solid ${C.amber}33`, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                        <span style={{ fontSize:14, color:C.amber }}>{pilier.icon}</span>
                      </div>
                      {/* Label */}
                      <span style={{ flex:1, fontSize:13, fontWeight:700, color:"var(--ink)", letterSpacing:"-.01em" }}>{pilier.label}</span>
                      {/* Badge actif */}
                      {active>0 && <span style={{ fontSize:9, padding:"2px 6px", borderRadius:999, background:`${C.amber}20`, color:C.amber, fontWeight:800, fontFamily:"'IBM Plex Mono',monospace" }}>{active}</span>}
                      {/* Chevron */}
                      <span style={{ fontSize:11, color:"var(--muted)", transform:isOpen?"rotate(180deg)":"none", transition:"transform .2s", flexShrink:0 }}>▼</span>
                    </button>
                    {/* Contenu filtres */}
                    {isOpen && (
                      <div style={{ padding:"4px 16px 16px", borderTop:"1px solid var(--line2)", display:"flex", flexDirection:"column", gap:14 }}>
                        {pilier.filters.map(f=>(
                          <div key={f.id}>
                            {f.type==="pills" ? (
                              <>
                                <div style={{ display:"flex", alignItems:"center", gap:5, marginBottom:7, marginTop:8 }}>
                                  <div style={{ fontSize:9, color:"var(--muted)", fontWeight:800, textTransform:"uppercase", letterSpacing:".09em", fontFamily:"'IBM Plex Mono',monospace" }}>{f.label}</div>
                                  {f.tt && <InvelpsTipMark title={f.tt}/>}
                                </div>
                                <ScPill options={f.options} value={fv[f.id]} onChange={v=>setFV(f.id,v)}/>
                              </>
                            ) : (
                              <div style={{ marginTop:8 }}>
                                <div style={{ display:"flex", alignItems:"center", gap:5, marginBottom:5 }}>
                                  <div style={{ fontSize:9, color:"var(--muted)", fontWeight:800, textTransform:"uppercase", letterSpacing:".09em", fontFamily:"'IBM Plex Mono',monospace" }}>{f.label}</div>
                                  {f.tt && <InvelpsTipMark title={f.tt}/>}
                                </div>
                                <ScSlider f={f} value={fv[f.id]} onChange={v=>setFV(f.id,v)}/>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
              {/* Reset mobile */}
              {isMobile && (
                <button onClick={()=>setFvAll(defFilters)} style={{ padding:"12px", fontSize:12, border:`1px solid ${C.red}44`, background:`${C.red}0e`, color:C.red, borderRadius:12, fontWeight:700, cursor:"pointer" }}>↺ Réinitialiser tous les filtres</button>
              )}
            </div>
          </div>
        )}

        {/* Résultats */}
        <div style={{ flex:1, minWidth:0 }}>
          {/* Sort bar — mobile uniquement */}
          {isMobile && (
            <div style={{ display:"flex", gap:5, overflowX:"auto", WebkitOverflowScrolling:"touch", paddingBottom:8, marginBottom:8 }}>
              {[{k:"iscore",l:"IScore"},{k:"roic_vs_wacc",l:"EVA"},{k:"mos",l:"MOS"},{k:"tscore",l:"TScore"},{k:"bt_5y_cagr_tr",l:"CAGR5Y"},{k:"er_proxy",l:"ER AQR"},{k:"ytd_pct",l:"YTD"}].map(({k,l})=>{
                const a=sk===k;
                return <button key={k} onClick={()=>ts(k)} style={{ flexShrink:0, padding:"4px 10px", fontSize:10, borderRadius:4, border:`1px solid ${a?C.amber+"66":"var(--line)"}`, background:a?`${C.amber}14`:"var(--paper-alt)", color:a?C.amber:"var(--muted)", cursor:"pointer", fontFamily:"'IBM Plex Mono',monospace", display:"flex", alignItems:"center", gap:3 }}>
                  {l}{a&&<span style={{fontSize:7}}>{sd==="desc"?"▼":"▲"}</span>}
                </button>;
              })}
            </div>
          )}

          {filtered.length===0 ? (
            <div style={{ padding:"40px", textAlign:"center", border:"1px solid var(--line)", borderRadius:12, background:"var(--paper)" }}>
              <div style={{ fontSize:20, color:"var(--muted)", marginBottom:6 }}>◌</div>
              <div style={{ fontSize:12, color:"var(--muted)" }}>Aucun résultat — <button onClick={()=>setFvAll(defFilters)} style={{background:"none",border:"none",color:C.amber,cursor:"pointer",fontSize:12}}>↺ Reset</button></div>
            </div>
          ) : isMobile ? (
            /* ── MOBILE : cartes empilées ── */
            <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
              {paginated.map(s => {
                const sigC = signalTone(s.dd_signal)==="pos"?C.green:signalTone(s.dd_signal)==="neg"?C.red:C.amber;
                return (
                  <button key={s.symbol} onClick={()=>openTicker(s.symbol)}
                    style={{ display:"block", width:"100%", background:"var(--paper)", border:"1px solid var(--line)", borderRadius:12, padding:"11px 13px", cursor:"pointer", textAlign:"left" }}
                    onMouseEnter={e=>e.currentTarget.style.background="var(--row-h)"}
                    onMouseLeave={e=>e.currentTarget.style.background="var(--paper)"}>
                    {/* Ligne 1 : ticker + signal */}
                    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:5 }}>
                      <div>
                        <div style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:13, fontWeight:800, color:"var(--ink)" }}>{s.symbol}</div>
                        <div style={{ fontSize:10, color:"var(--muted)", marginTop:1 }}>{s.company.slice(0,28)}</div>
                      </div>
                      <div style={{ display:"flex", flexDirection:"column", alignItems:"flex-end", gap:4 }}>
                        <span style={{ fontSize:9, fontWeight:800, padding:"2px 7px", borderRadius:3, background:`${sigC}18`, border:`1px solid ${sigC}44`, color:sigC, fontFamily:"'IBM Plex Mono',monospace" }}>{s.dd_signal}</span>
                        <div style={{ display:"flex", gap:2 }}>
                          {Array.from({length:s.dd_yes}).map((_,j)=><span key={"y"+j} style={{width:5,height:5,borderRadius:2,background:C.green}}/>)}
                          {Array.from({length:s.dd_watch}).map((_,j)=><span key={"w"+j} style={{width:5,height:5,borderRadius:2,background:C.amber}}/>)}
                          {Array.from({length:s.dd_no}).map((_,j)=><span key={"n"+j} style={{width:5,height:5,borderRadius:2,background:C.red}}/>)}
                        </div>
                      </div>
                    </div>
                    {/* Ligne 2 : métriques clés */}
                    <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:6, marginTop:6 }}>
                      {[
                        {l:"IScore", v:s.iscore.toFixed(1), c:scoreColor(s.iscore)},
                        {l:"EVA", v:`${s.roic_vs_wacc>0?"+":""}${s.roic_vs_wacc.toFixed(1)}`, c:evaColor(s.roic_vs_wacc)},
                        {l:"MOS", v:`${Math.min(999,s.mos).toFixed(0)}%`, c:mosColor(s.mos)},
                        {l:"TScore", v:s.tscore.toFixed(0), c:scoreColor(s.tscore)},
                      ].map(({l,v,c})=>(
                        <div key={l} style={{ background:"var(--paper-alt)", borderRadius:7, padding:"5px 7px", border:"1px solid var(--line2)" }}>
                          <div style={{ fontSize:8, color:"var(--muted)", fontFamily:"'IBM Plex Mono',monospace", textTransform:"uppercase", letterSpacing:".08em" }}>{l}</div>
                          <div style={{ fontSize:12, fontWeight:800, color:c, fontFamily:"'IBM Plex Mono',monospace", marginTop:2 }}>{v}</div>
                        </div>
                      ))}
                    </div>
                    {/* Ligne 3 : CAGR + ER + YTD + Moat */}
                    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginTop:7, paddingTop:7, borderTop:"1px solid var(--line2)" }}>
                      <div style={{ display:"flex", gap:10 }}>
                        {[
                          {l:"CAGR5Y", v:`${s.bt_5y_cagr_tr>0?"+":""}${s.bt_5y_cagr_tr.toFixed(1)}%`, c:cagrColor(s.bt_5y_cagr_tr)},
                          {l:"ER", v:`${s.er_proxy>0?"+":""}${s.er_proxy.toFixed(1)}%`, c:erColor(s.er_proxy)},
                          {l:"YTD", v:`${s.ytd_pct>0?"+":""}${s.ytd_pct.toFixed(1)}%`, c:s.ytd_pct>=0?C.green:C.red},
                        ].map(({l,v,c})=>(
                          <div key={l}>
                            <div style={{ fontSize:8, color:"var(--muted)", fontFamily:"'IBM Plex Mono',monospace" }}>{l}</div>
                            <div style={{ fontSize:11, fontWeight:800, color:c, fontFamily:"'IBM Plex Mono',monospace" }}>{v}</div>
                          </div>
                        ))}
                      </div>
                      <span style={{ fontSize:9, padding:"2px 7px", borderRadius:3, fontFamily:"'IBM Plex Mono',monospace", fontWeight:700,
                        background:s.moat==="Wide"?`${C.green}18`:s.moat==="Narrow"?`${C.amber}18`:`${C.red}18`,
                        color:s.moat==="Wide"?C.green:s.moat==="Narrow"?C.amber:C.red,
                        border:`1px solid ${s.moat==="Wide"?C.green:s.moat==="Narrow"?C.amber:C.red}44` }}>{s.moat}</span>
                    </div>
                  </button>
                );
              })}
            </div>
          ) : (
            /* ── DESKTOP : tableau horizontal ── */
            <div style={{ overflowX:"auto", WebkitOverflowScrolling:"touch", borderRadius:12, border:"1px solid var(--line)", background:"var(--paper)" }}>
              <div style={{ minWidth: SC_COLS.reduce((s,c)=>s+c.w,0)+28 }}>
                <div style={{ display:"flex", alignItems:"center", padding:"6px 14px", borderBottom:"2px solid var(--line)", background:"var(--paper-alt)", position:"sticky", top:0, zIndex:10 }}>
                  {SC_COLS.map((col,i)=>(
                    <div key={i} onClick={()=>col.key&&ts(col.key)} style={{
                      width:col.w, flexShrink:0, fontSize:8, fontFamily:"'IBM Plex Mono',monospace",
                      letterSpacing:".10em", textTransform:"uppercase",
                      color:sk===col.key?C.amber:"var(--muted)",
                      cursor:col.key?"pointer":"default", userSelect:"none",
                      display:"flex", alignItems:"center", gap:2,
                      justifyContent:i>1?"flex-end":"flex-start",
                    }}>
                      {col.label}{col.key&&sk===col.key&&<span style={{fontSize:7}}>{sd==="desc"?"▼":"▲"}</span>}
                    </div>
                  ))}
                </div>
                {paginated.map((s) => (
                  <button key={s.symbol} onClick={()=>openTicker(s.symbol)}
                    style={{ display:"flex", alignItems:"center", padding:"9px 14px", width:"100%", background:"none", border:"none", borderTop:"1px solid var(--line2)", cursor:"pointer", textAlign:"left" }}
                    onMouseEnter={e=>e.currentTarget.style.background="var(--row-h)"}
                    onMouseLeave={e=>e.currentTarget.style.background="none"}>
                    <div style={{ width:148, flexShrink:0 }}>
                      <div style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:11, fontWeight:800, color:"var(--ink)", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{s.symbol}</div>
                      <div style={{ fontSize:9, color:"var(--muted)", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{s.company}</div>
                    </div>
                    <div style={{ width:80, flexShrink:0 }} onClick={e=>e.stopPropagation()}>
                      <ScDecisionTooltip stock={s} th={th}/>
                    </div>
                    <div style={{ width:64, flexShrink:0, textAlign:"right" }}><span style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:13, fontWeight:800, color:scoreColor(s.iscore) }}>{s.iscore.toFixed(1)}</span></div>
                    <div style={{ width:56, flexShrink:0, textAlign:"right" }}><span style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:11, fontWeight:800, color:scoreColor(s.fund_score) }}>{s.fund_score.toFixed(0)}</span></div>
                    <div style={{ width:60, flexShrink:0, textAlign:"right" }}><span style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:11, fontWeight:800, color:evaColor(s.roic_vs_wacc) }}>{s.roic_vs_wacc>0?"+":""}{s.roic_vs_wacc.toFixed(1)}</span></div>
                    <div style={{ width:70, flexShrink:0, textAlign:"right" }}><span style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:11, fontWeight:800, color:mosColor(s.mos) }}>{Math.min(999,s.mos).toFixed(0)}%</span></div>
                    <div style={{ width:60, flexShrink:0, textAlign:"right" }}><span style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:11, fontWeight:800, color:scoreColor(s.tscore) }}>{s.tscore.toFixed(0)}</span></div>
                    <div style={{ width:66, flexShrink:0, textAlign:"right" }}><span style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:11, fontWeight:800, color:cagrColor(s.bt_5y_cagr_tr) }}>{s.bt_5y_cagr_tr>0?"+":""}{s.bt_5y_cagr_tr.toFixed(1)}%</span></div>
                    <div style={{ width:62, flexShrink:0, textAlign:"right" }}><span style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:11, fontWeight:800, color:erColor(s.er_proxy) }}>{s.er_proxy>0?"+":""}{s.er_proxy.toFixed(1)}%</span></div>
                    <div style={{ width:56, flexShrink:0, textAlign:"right" }}><span style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:11, fontWeight:800, color:s.ytd_pct>=0?C.green:C.red }}>{s.ytd_pct>0?"+":""}{s.ytd_pct.toFixed(1)}%</span></div>
                    <div style={{ width:60, flexShrink:0, textAlign:"right" }}>
                      <span style={{ fontSize:9, padding:"2px 6px", borderRadius:3, fontFamily:"'IBM Plex Mono',monospace", fontWeight:700,
                        background:s.moat==="Wide"?`${C.green}18`:s.moat==="Narrow"?`${C.amber}18`:`${C.red}18`,
                        color:s.moat==="Wide"?C.green:s.moat==="Narrow"?C.amber:C.red,
                        border:`1px solid ${s.moat==="Wide"?C.green:s.moat==="Narrow"?C.amber:C.red}44` }}>{s.moat}</span>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}
          {/* Pagination */}
          {totalPages > 1 && (
            <div style={{ display:"flex", alignItems:"center", justifyContent:"center", gap:6, marginTop:10 }}>
              <button onClick={()=>setPage(p=>Math.max(0,p-1))} disabled={page===0} style={{ padding:"5px 12px", fontSize:11, borderRadius:6, cursor:page===0?"not-allowed":"pointer", border:`1px solid ${page===0?"var(--line)":C.amber+"66"}`, background:page===0?"transparent":`${C.amber}12`, color:page===0?"var(--muted)":C.amber, fontFamily:"'IBM Plex Mono',monospace", opacity:page===0?0.4:1 }}>← Préc.</button>
              <span style={{ fontSize:10, color:"var(--muted)", fontFamily:"'IBM Plex Mono',monospace" }}>{page+1}/{totalPages}</span>
              <button onClick={()=>setPage(p=>Math.min(totalPages-1,p+1))} disabled={page===totalPages-1} style={{ padding:"5px 12px", fontSize:11, borderRadius:6, cursor:page===totalPages-1?"not-allowed":"pointer", border:`1px solid ${page===totalPages-1?"var(--line)":C.amber+"66"}`, background:page===totalPages-1?"transparent":`${C.amber}12`, color:page===totalPages-1?"var(--muted)":C.amber, fontFamily:"'IBM Plex Mono',monospace", opacity:page===totalPages-1?0.4:1 }}>Suiv. →</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


/* ─── NOT FOUND ───────────────────────────────────────────────── */
function NotFound({ nav }) {
  return (
    <div style={{ maxWidth:400, margin:"80px auto", padding:"0 14px", textAlign:"center" }}>
      <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:52, color:"var(--muted)", lineHeight:1 }}>◌</div>
      <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:22, color:"var(--ink)", margin:"12px 0 8px" }}>Page introuvable</div>
      <button onClick={()=>nav("/")} style={{ padding:"10px 22px", borderRadius:12, border:"none", background:"linear-gradient(135deg,#3d7068,#2e574f)", color:"#fff", fontSize:13, fontWeight:800 }}>← Accueil</button>
    </div>
  );
}

/* ─── REDIRECT legacy /stock/:sym → analyse titre ─────────────── */
function RedirectToTicker({ sym }) {
  const navigate = useNavigate();
  useEffect(() => {
    navigate(`/marketflow/ticker/${encodeURIComponent(sym)}`, { replace: true });
  }, [sym, navigate]);
  return (
    <div style={{ padding: 48, textAlign: "center", color: "var(--muted)", fontSize: 14 }}>
      Redirection vers l’analyse titre…
    </div>
  );
}

/* ─── ROOT ────────────────────────────────────────────────────── */
export default function InvelpsCapitalApp() {
  const { path, nav } = useCapitalRouter();
  const navigate = useNavigate();
  const { r, p } = getRoute(path);
  const [isDark, setDark] = useState(() => {
    const h = new Date().getHours();
    return h < 6 || h >= 21;
  });
  const th = isDark ? DARK : LIGHT;
  const [apiHome, setApiHome] = useState(null);

  const homeBundle = useMemo(() => {
    if (apiHome && !apiHome.error) {
      const b = buildHomeBundleFromApi(apiHome);
      if (b) return b;
    }
    return getStaticHomeBundle();
  }, [apiHome]);

  const openTicker = useCallback(
    (sym) => navigateToStockDashboard(navigate, sym, "capital"),
    [navigate]
  );

  useEffect(() => {
    syncTheme(isDark ? DARK : LIGHT, isDark);
  }, [isDark]);

  useEffect(() => {
    if (!document.getElementById("inv-css")) {
      const s = document.createElement("style");
      s.id = "inv-css";
      s.textContent = CSS;
      document.head.appendChild(s);
    }
  }, []);

  useEffect(() => {
    const base = (Constants.BACKEND_SERVER_BASE_URL || "").replace(/\/$/, "");
    if (!base) return;
    fetch(`${base}/capitalflow/homepage`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
      .then((j) => setApiHome(j))
      .catch(() => setApiHome({ error: true }));
  }, []);

  const page = () => {
    switch (r) {
      case "home":
        return <HomePage nav={nav} th={th} openTicker={openTicker} />;
      case "markets":
        return <MarketsPage th={th} />;
      case "screener":
        return <ScreenerPage nav={nav} th={th} openTicker={openTicker} />;
      case "strategies":
        return <IndexStrategiesPageContainer />;
      case "stock":
        return <RedirectToTicker sym={p.sym} />;
      default:
        return <NotFound nav={nav} />;
    }
  };

  return (
    <HomeBundleContext.Provider value={homeBundle}>
      <Shell path={path} th={th} isDark={isDark} setDark={setDark}>
        {page()}
      </Shell>
    </HomeBundleContext.Provider>
  );
}
